import { __decorate } from "tslib";
import { PrintRoleType, OrderRegisterType, OrderStatusType, PaymentTypeLabel, OrderRegisterStatusType, OrderFlagType, ClientIDType, CustomPaymentTypeLabel } from '../types';
import { User, Table, Client, OrderItem, TaxRate, TaxItem, Product, ProductIngredient, Shift, CashRegister, KitchenTicket, Sector, PriceCalculation, TableSession } from '../models';
import { Model, modelRegistry } from './Model';
import { apiService } from '../services/ApiService';
import { connectionService } from '../services/ConnectionService';
import { fiscalService } from '../fiscal/FiscalService';
import { diContainer } from '../DIContainer';
import AppService from '../services/AppService';
import OrderService from '../services/OrderService';
import EinvoiceService from '../services/EinvoiceService';
import TableSessionService from '../services/TableSessionService';
import { settlementPresenter } from '../services/SettlementPresenter';
import HashGen from '../utilities/HashGen';
import Money from '../utilities/Money';
import LatToCyr from '../utilities/LatToCyr';
import Printable from '../print/Printable';
import { receiptLogoFor } from '../print/receiptLogo';
import PrintService from '../print/PrintService';
import Store from '../wrappers/Store';
import { I18n, LocaleOverride } from '../wrappers/I18n';
import { dateFormatter } from '../utilities/dateFormatter';
import { OrderValidation, vatExemptNote } from '../utilities';
import AdvanceFlowService from '../services/AdvanceFlowService';
import moment from 'moment-timezone';
const { formatDateTime, formatDateTimeHuman } = dateFormatter();
export class Order extends Model {
    static entity = 'orders';
    static primaryKey = 'hash';
    static syncable = true;
    static get appStorage() {
        return diContainer.resolve('appStorage');
    }
    static get errorReporter() {
        return diContainer.resolve('errorReporter');
    }
    /** The server's answers still on their way, by order hash — sync() may return its 2s default first. */
    static settling = new Map();
    /**
     * Resolves once the server has answered this order's latest sync — at once when none is in flight
     * (answered already, or queued: then the order still has no id).
     */
    static settled(hash) {
        return Order.settling.get(hash) ?? Promise.resolve();
    }
    id = null;
    hash = '';
    number = null;
    internal_number = null;
    shift_id = null;
    shift_hash = '';
    cash_register_id = null;
    user_id = null;
    table_id = null;
    client_id = null;
    status = OrderStatusType.new;
    paid_cash = '0.0000';
    paid_credit_card = '0.0000';
    paid_credit_card_business = '0.0000';
    paid_invoice = '0.0000';
    paid_advance = '0.0000';
    paid_check = '0.0000';
    paid_voucher = '0.0000';
    paid_mobile_money = '0.0000';
    paid_other_noncash = '0.0000';
    change = '0.0000';
    tip = '0.0000';
    taxes = [];
    export = false;
    iic = null;
    fiscal_code = null;
    source = null;
    register_type = null;
    register_status = null;
    register_number = null;
    register_request = null;
    register_response = null;
    registered_at = null;
    canceled_by_user_id = null;
    canceled_at = null;
    note = null;
    due_date = null;
    start_date = null;
    end_date = null;
    tax_period = null;
    flag = null;
    incoming = false;
    settled_by = null;
    external_synced_at = null;
    settlement_data = null;
    last_activity_at = null;
    created_at = null;
    updated_at = null;
    items = [];
    refers_to = [];
    referred_by = [];
    reference;
    shift;
    cashRegister;
    user;
    table;
    client;
    canceledByUser;
    priceCalculation;
    credit_note_referrals = [];
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.long()),
            number: this.attr(null),
            internal_number: this.attr(null),
            shift_id: this.attr(null),
            shift_hash: this.attr(null),
            cash_register_id: this.attr(null, value => {
                let curCashRegister = CashRegister.current();
                return !value && curCashRegister ? curCashRegister.id : value;
            }),
            user_id: this.attr(null),
            table_id: this.attr(null),
            client_id: this.attr(null),
            status: this.attr(OrderStatusType.new),
            paid_cash: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_credit_card: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_credit_card_business: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_invoice: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_advance: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_check: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_voucher: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_mobile_money: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            paid_other_noncash: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            change: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            tip: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            taxes: this.attr([], value => value ? value.map((item) => new TaxItem(item)) : []),
            export: this.attr(false, value => !!value),
            iic: this.attr(null),
            fiscal_code: this.attr(null),
            source: this.attr(null),
            register_type: this.attr(null),
            register_status: this.attr(null),
            register_number: this.attr(null),
            register_request: this.attr(null),
            register_response: this.attr(null),
            registered_at: this.attr(null),
            canceled_by_user_id: this.attr(null),
            canceled_at: this.attr(null),
            note: this.attr(null),
            due_date: this.attr(null),
            start_date: this.attr(null),
            end_date: this.attr(null),
            tax_period: this.attr(null),
            flag: this.attr(null),
            incoming: this.attr(false, value => !!value),
            settled_by: this.attr(null),
            external_synced_at: this.attr(null),
            settlement_data: this.attr(null),
            last_activity_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            items: this.attr([], (value) => (value || []).map(item => new OrderItem(item))),
            refers_to: this.attr([], (value) => (value || []).map(item => new Order(item))),
            referred_by: this.attr([], (value) => (value || []).map(item => new Order(item))),
            reference: this.attr(null),
            shift: this.attr(undefined, value => value ? new Shift(value) : undefined),
            cashRegister: this.attr(undefined, value => value ? new CashRegister(value) : undefined),
            user: this.attr(undefined, value => value ? new User(value) : undefined),
            table: this.attr(undefined, value => value ? new Table(value) : undefined),
            client: this.attr(undefined, value => value ? new Client(value) : undefined),
            canceledByUser: this.attr(undefined, value => value ? new User(value) : undefined),
            priceCalculation: this.attr(undefined, value => value ? new PriceCalculation(value) : undefined),
            credit_note_referrals: this.attr([]),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    static fiscalStatuses() {
        return [];
    }
    getRegisterRequest() {
        return undefined;
    }
    shouldRegister() {
        return false;
    }
    async register(perfTracker) {
        return { success: false };
    }
    shouldPrintReceipt() {
        return !this.isUnpaid() && !this.flagsInclude('closeAdvance');
    }
    /** The NBS IPS payment QR payload (a market override); `venue` is the receipt's own venue. */
    getPaymentQRCode(_venue) {
        return null;
    }
    /** Kitchen and order slips, and the KDS ticket — never for an e-invoice or a credit note. */
    shouldPrintSlips() {
        return !this.isEinvoice() && !this.isCreditNote() && (this.isUnpaid() || !this.paysForOrders().length);
    }
    async sync(action, perfTracker) {
        if (this.isEinvoice()) {
            perfTracker?.startStep('einvoice_send');
            const result = await this.einvoiceSend();
            perfTracker?.endStep(); // einvoice_send
            // Auto-close check — einvoice settlement is a plain await, so the
            // store state is settled here (einvoiceSend inserts order + ref_orders)
            if (result.status !== 'failed') {
                TableSessionService.handleAutoClose(this).catch(() => { });
            }
            return result;
        }
        if (this.shouldRegister() && action === 'create') {
            perfTracker?.startStep('fiscal_registration');
            const registerResult = await this.register(perfTracker);
            perfTracker?.endStep(); // fiscal_registration
            if (!registerResult.success) {
                return { status: 'failed', error: registerResult.error };
            }
        }
        perfTracker?.startStep('mark_for_sync');
        const syncData = await this.markForSync(action);
        perfTracker?.endStep(); // mark_for_sync
        // The server has never seen an order without an id (issued offline; the
        // id arrives on queue replay). Whatever depends on such an order is
        // queued behind its pending create even while online:
        // - its own update/delete/cancel — sent live it returns not_found, and
        //   that path strips every queued entry for this hash, the create
        //   included, so the order would never reach the server at all;
        // - a corrective of it — sent live the server cannot resolve the
        //   corrected order and denies it, after the replacement round it
        //   belongs to has already been issued, leaving both on the table;
        // - an advance closing that names such a final (`advanceFinal`) — the
        //   server builds EFI's IICRefs from the final it already holds.
        // An order with an id, and a corrective of orders with ids, take the
        // online path exactly as before.
        const queuedBehindCreate = action !== 'create'
            ? !this.id
            : this.correctsOrders().some(order => !order.id)
                || this.refers_to.some(order => order.reference?.type === 'advanceFinal' && !(Order.find(order.hash)?.id ?? order.id));
        if (queuedBehindCreate || !connectionService.isOnline) {
            perfTracker?.startStep('offline_processing');
            const productCounts = this.getUpdatedProductCounts(action);
            if (productCounts.length)
                Product.update(productCounts);
            this.$save();
            this.onAfterSave(action);
            await syncData?.saveForSyncBackup();
            // Each referred order goes as stored here (stamped, its references kept, this one added): this
            // order's bare copy of it would cost the other registers its references. An order this register
            // does not hold (the store keeps recent orders) goes as the copy, as before.
            Order.syncTerminals({
                items: [
                    { action: 'post', record: this.$toJson() },
                    ...this.refers_to.map(order => ({ action: 'patch', record: (Order.find(order.hash) ?? order).$toJson() })),
                ],
                additional: {
                    product_counts: productCounts,
                    client: this.getClient()?.$toJson()
                }
            });
            perfTracker?.endStep(); // offline_processing
            // Auto-close check against locally settled state (offline path)
            TableSessionService.handleAutoClose(this).catch(() => { });
            return { status: 'success', order: this };
        }
        perfTracker?.startStep('online_sync');
        const timeLimit = 2000;
        const defaultResult = { status: 'success', default: true, order: this };
        const promise1 = new Promise(resolve => setTimeout(() => resolve(defaultResult), timeLimit));
        perfTracker?.startStep('sync_exec');
        const promise2 = this.syncExec(action, syncData, perfTracker);
        const hash = this.hash;
        const settling = promise2.then(() => undefined, () => undefined);
        Order.settling.set(hash, settling);
        settling.then(() => { if (Order.settling.get(hash) === settling)
            Order.settling.delete(hash); });
        // Auto-close check anchored to real sync settlement — the raced
        // 2s default can return before the store reflects paid/canceled
        // tabs, so the check must not run off the raced result.
        promise2.then(() => TableSessionService.handleAutoClose(this)).catch(() => { });
        // A corrective of an externally settled order awaits the real response:
        // the server reverses the settlement inside this request and reports the
        // outcome on the returned order, which the 2s local default never carries.
        const awaitsStornoOutcome = this.correctsOrders().some(order => order.isSettledExternally());
        const syncResult = awaitsStornoOutcome
            ? await promise2
            : await Promise.race([promise1, promise2]);
        perfTracker?.endStep(); // sync_exec
        if (syncResult.default) {
            delete syncResult.default;
            this.setFlag(OrderFlagType.timeout, 'true');
            Order.errorReporter.reportMessage(`Order post longer than ${timeLimit}ms`);
        }
        perfTracker?.endStep(); // online_sync
        return syncResult;
    }
    async syncExec(action, syncData, perfTracker) {
        try {
            perfTracker?.startStep('http_post');
            const response = await apiService.post('/orders/process', { order: this.$toJson(), action });
            perfTracker?.endStep(); // http_post
            const orderTimedOut = !!this.getFlag(OrderFlagType.timeout)[0];
            perfTracker?.startStep('process_response');
            // if order was returned we need to make sure it's stored on the client
            // if the returned order is not the this one, than this one needs to be dropped
            if (response.data.order) {
                Order.syncTerminals({
                    items: [
                        { action: 'post', record: response.data.order },
                        ...(response.data.ref_orders || []).map(order => ({ action: 'patch', record: order })),
                    ],
                    additional: {
                        product_counts: response.data.product_counts || [],
                        client: response.data.client || undefined
                    }
                });
                if (response.data.order.hash === this.hash) {
                    this.$fill(response.data.order);
                }
                Order.insert({ data: [response.data.order] });
            }
            if (response.data.ref_orders?.length) {
                Order.insert({ data: response.data.ref_orders });
            }
            if (response.data.client) {
                Client.update(response.data.client);
            }
            if (response.data.product_counts?.length) {
                Product.update(response.data.product_counts);
            }
            perfTracker?.endStep(); // process_response
            const nonBlockingError = ['already_exists', 'duplicate'].includes(response.data.error || '');
            const fiscalRegisteredButNotSynced = this.isFiscalRegistered() && response.data.error && !nonBlockingError;
            const orderLost = fiscalRegisteredButNotSynced || (orderTimedOut && response.data.error);
            if (fiscalRegisteredButNotSynced) {
                setTimeout(() => AppService.sync(), 5000);
            }
            else {
                await this.removeFromSync();
            }
            if (orderLost) {
                await syncData?.saveForSyncBackup();
                Order.errorReporter.reportMessage('Order lost' + (orderTimedOut ? ': timeout' : ''), {
                    order: this.jsonToLog(),
                    error: response.data.error
                });
            }
            if (response.data.error) {
                return {
                    status: nonBlockingError || this.isFiscalRegistered() ? 'successWithErrors' : 'failed',
                    error: I18n.t(`order.error.${response.data.error}`)
                };
            }
            return { status: 'success', order: this };
        }
        catch (error) {
            perfTracker?.startStep('exception_handling');
            const productCounts = this.getUpdatedProductCounts(action);
            if (productCounts.length)
                Product.update(productCounts);
            this.$save();
            this.onAfterSave(action);
            await syncData?.saveForSyncBackup();
            console.log(error.message);
            Order.errorReporter.reportError(error, { order: this.jsonToLog() });
            const isOnline = await connectionService.connectionCheckRetry(5000);
            perfTracker?.endStep(); // exception_handling
            if (isOnline) {
                return { status: 'success', order: this };
            }
            return {
                status: 'successWithErrors',
                error: I18n.t('order.error.order_process_failed')
            };
        }
    }
    jsonToLog() {
        const json = this.$toJson();
        json.items = JSON.stringify(json.items);
        json.refers_to = JSON.stringify(json.refers_to);
        json.referred_by = JSON.stringify(json.referred_by);
        json.taxes = JSON.stringify(json.taxes);
        return json;
    }
    /**
     * Fetch new orders from server
     */
    static async fetchLatest() {
        const lastUpdatedAt = Order.getLastUpdatedAt();
        const params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        const response = await apiService.get('/orders/latest', { params: params });
        Order.insert({ data: response.data.orders });
    }
    /**
     * Create a new order instance
     */
    static async createNew() {
        const curShift = Shift.current();
        const curUser = User.current();
        return new Order({
            hash: HashGen.long(),
            number: await OrderService.getNextNumber(),
            shift_id: curShift?.id || null,
            shift_hash: curShift?.hash || null,
            user_id: curUser?.id || null,
        });
    }
    /**
     * Create a new order instance from advance order
     */
    static async createFromAdvance(orders, options) {
        const { linked = false } = options;
        const order = orders[0];
        if (linked) {
            const freshOrder = await Order.createNew();
            freshOrder.status = OrderStatusType.advance;
            const registeredAt = moment
                .utc(order.registered_at)
                .tz('Europe/Belgrade')
                .format('YYYY-MM-DD HH:mm:ss');
            if (order.client_id) {
                freshOrder.client_id = order.client_id;
            }
            if (order.register_number) {
                freshOrder.setFlag(OrderFlagType.ref, order.register_number, registeredAt);
                // Issued at a negative amount it refunds part of this advance: the link tells the advance what
                // is left of it (onBeforeIssue keeps it only then). Its fiscal reference stays the flag.
                freshOrder.addReference(order, 'linkedRefund');
            }
            return freshOrder;
        }
        const inParts = I18n.c('order.advanceInParts');
        const freshOrder = await order.fresh(false, I18n.c('order.allowAdvanceClose'));
        orders.forEach(o => freshOrder.addReference(o, 'advance'));
        if (order.client_id) {
            freshOrder.client_id = order.client_id;
        }
        // An advance used in parts: a final from it is not a table's bill — the hospitality table
        // reload would merge the table's open rounds into it and put the table's buyer on it.
        if (inParts) {
            freshOrder.table_id = null;
        }
        freshOrder.items = [];
        // What is left is read from the stored advance (a receipt-list row may predate its latest
        // closings). An advance this register does not hold yet (one from another day) is stored from
        // the row, so the final's closing finds it — also when the final goes to the queue.
        if (inParts)
            orders.filter(advance => !Order.find(advance.hash)).forEach(advance => Order.insert({ data: [advance.$toJson()] }));
        const remaining = (advance) => inParts
            ? AdvanceFlowService.left(Order.find(advance.hash), freshOrder)
            : advance.getRemainingAdvanceAmount().abs();
        const paidAdvance = orders.reduce((acc, cur) => acc.add(remaining(cur)), Money.parse(0)).toFormat('0.0000');
        freshOrder.setFlag(OrderFlagType.advance, paidAdvance);
        freshOrder.taxes = Order.calculateTaxes(freshOrder);
        return freshOrder;
    }
    /**
     * Make a duplicate of the current order.
     */
    $duplicate() {
        return new Order(this.$toJson());
    }
    /**
     * Snapshot of the order for the sync queue, holding at most ONE level of
     * references: each embedded order in refers_to/referred_by keeps its
     * scalar fields (hash, reference, register_*, ...) but has empty
     * refers_to/referred_by of its own. The deep clone detaches the snapshot
     * from the live order graph — onAfterSave mutates the live reference
     * arrays after markForSync, and an aliased payload would be deepened
     * retroactively while already queued.
     */
    static toSyncSafeJson(record) {
        const clone = JSON.parse(JSON.stringify(record));
        const flatten = (refs) => (refs || []).map(ref => ({ ...ref, refers_to: [], referred_by: [] }));
        clone.refers_to = flatten(clone.refers_to);
        clone.referred_by = flatten(clone.referred_by);
        return clone;
    }
    $toSyncJson() {
        return Order.toSyncSafeJson(this.$toJson());
    }
    /**
     * Create a fresh copy of order
     */
    async fresh(copyPayments = false, reverse = false, includeRefs = false) {
        let freshOrder = await Order.createNew();
        freshOrder.client_id = this.client_id;
        freshOrder.table_id = this.table_id;
        freshOrder.export = this.export;
        freshOrder.note = this.note;
        freshOrder.due_date = this.due_date;
        freshOrder.start_date = this.start_date;
        freshOrder.end_date = this.end_date;
        freshOrder.tax_period = this.tax_period;
        freshOrder.register_type = this.register_type;
        freshOrder.taxes = this.taxes;
        freshOrder.items = this.items.map(item => item.fresh(freshOrder, reverse));
        if (copyPayments) {
            const multiplicator = !reverse ? 1 : -1;
            freshOrder.paid_cash = this.getPaidCash().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_credit_card = this.getPaidCreditCard().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_invoice = this.getPaidInvoice().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_credit_card_business = this.getPaidCreditCardBusiness().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_advance = this.getPaidAdvance().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_check = this.getPaidCheck().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_voucher = this.getPaidVoucher().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_mobile_money = this.getPaidMobileMoney().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_other_noncash = this.getPaidOtherNoncash().multiply(multiplicator).toFormat('0.0000');
            if (!reverse) {
                freshOrder.change = this.change;
            }
        }
        if (includeRefs) {
            freshOrder.refers_to = this.refers_to;
            freshOrder.referred_by = this.referred_by;
        }
        return freshOrder;
    }
    onAfterSave(action) {
        if (action !== 'create')
            return;
        // Add reverse references for referred orders. This order's copy of each carries none of the
        // referred order's own references (addReference): the stored order keeps its own.
        const referredOrders = [];
        this.refers_to.forEach(order => {
            order.addReference(this, order.reference.type, true);
            const stored = Order.find(order.hash);
            referredOrders.push({ hash: order.hash, referred_by: [...(stored?.referred_by ?? []), ...order.referred_by] });
        });
        if (referredOrders.length) {
            Order.update(referredOrders);
        }
        // If there is cash exchanged update client debt
        if (this.isCashExchanged()) {
            const client = this.getClient();
            if (this.flagsInclude('installments') && client) {
                const debt = client
                    .getDebt()
                    .add(this.getPaidOtherNoncash())
                    .toFormat('0.0000');
                Client.update({ id: this.client_id, debt: debt });
            }
        }
    }
    shouldUpdateProductCounts() {
        const correctType = this.getFlag(OrderFlagType.correctType)[0];
        if (correctType === 'correctSummary' || correctType === 'cancelSummary')
            return false;
        if (this.isTraining())
            return false;
        if (this.paysForOrders().length)
            return false;
        return this.isPaid() || this.isUnpaid() || this.isCreditNote();
    }
    getUpdatedProductCounts(action) {
        if (!this.shouldUpdateProductCounts())
            return [];
        const productCounts = this.getProductCounts(action === 'cancel');
        const previousProductCounts = action === 'update' || action === 'delete'
            ? (Order.find(this.hash)?.getProductCounts() || [])
            : [];
        const products = Product.all().filter(p => [...productCounts, ...previousProductCounts].some(pc => pc.id === p.id));
        return products.map(product => {
            const reverseCount = previousProductCounts.find(pc => pc.id === product.id)?.count || 0;
            const newCount = productCounts.find(pc => pc.id === product.id)?.count || 0;
            return {
                id: product.id,
                count: product.getCount() + reverseCount - newCount
            };
        });
    }
    getProductCounts(reverse = false) {
        const productCounts = [];
        for (const orderItem of this.items) {
            const product = orderItem.getProduct();
            if (!product)
                continue;
            let count = !this.isCreditNote() ? orderItem.getCount() : orderItem.getAdditionalCount();
            if (reverse)
                count = -1 * count;
            if (!product.is_made) {
                const alreadyAdded = productCounts.find(p => p.id === product.id);
                if (alreadyAdded) {
                    alreadyAdded.count += count;
                }
                else {
                    productCounts.push({ id: product.id, count });
                }
            }
            if (product.is_made) {
                const ingredients = ProductIngredient.all().filter(ingredient => ingredient.super_product_id == product.id);
                for (const ingredient of ingredients) {
                    const ingredientProduct = Product.find(ingredient.product_id);
                    if (!ingredientProduct)
                        continue;
                    const alreadyAdded = productCounts.find(p => p.id === ingredientProduct.id);
                    if (alreadyAdded) {
                        alreadyAdded.count += count * ingredient.getQuantity();
                    }
                    else {
                        productCounts.push({
                            id: ingredientProduct.id,
                            count: count * ingredient.getQuantity()
                        });
                    }
                }
            }
        }
        return productCounts;
    }
    setPayments(payments) {
        payments = payments.filter(p => p.key !== PaymentTypeLabel.advance);
        const paid = {
            cash: Money.parse(0),
            credit_card: Money.parse(0),
            invoice: Money.parse(0),
            credit_card_business: Money.parse(0),
            advance: this.getPaidAdvanceEffective(),
            check: Money.parse(0),
            voucher: Money.parse(0),
            mobile_money: Money.parse(0),
            other_noncash: Money.parse(0),
            change: Money.parse(0),
            tip: Money.parse(0)
        };
        let toPay = this.getLeftToPay();
        if (payments.length === 1 && payments[0].key === PaymentTypeLabel.cash) {
            const change = Money.parse(payments[0].amount).subtract(toPay);
            if (change.toUnit() > 0.01)
                paid.change = change;
            payments[0].amount = toPay.toRoundedUnit(4);
        }
        if (payments.length === 1 && payments[0].key === PaymentTypeLabel.credit_card) {
            const tip = Money.parse(payments[0].amount).subtract(toPay);
            if (tip.toUnit() > 0.01)
                paid.tip = tip;
            payments[0].amount = toPay.toRoundedUnit(4);
        }
        // correct potential issues in amount
        payments.forEach((payment, index) => {
            let amount = Money.parse(payment.amount);
            if (amount.toUnit() > toPay.toUnit()) {
                amount = Money.parse(toPay.toUnit());
            }
            else if (toPay.toUnit() >= 0 && amount.toUnit() < 0) {
                amount = Money.parse(0);
            }
            if (index === payments.length - 1) {
                paid[payment.key] = toPay;
            }
            else {
                paid[payment.key] = amount;
                toPay = toPay.subtract(amount);
            }
        });
        this.paid_cash = paid.cash.toFormat('0.0000');
        this.paid_credit_card = paid.credit_card.toFormat('0.0000');
        this.paid_invoice = paid.invoice.toFormat('0.0000');
        this.paid_credit_card_business = paid.credit_card_business.toFormat('0.0000');
        this.paid_advance = paid.advance.toFormat('0.0000');
        this.paid_check = paid.check.toFormat('0.0000');
        this.paid_voucher = paid.voucher.toFormat('0.0000');
        this.paid_mobile_money = paid.mobile_money.toFormat('0.0000');
        this.paid_other_noncash = paid.other_noncash.toFormat('0.0000');
        this.change = paid.change.toFormat('0.0000');
        this.tip = paid.tip.toFormat('0.0000');
    }
    static getPayOptions(order) {
        return {
            status: order?.status,
            table: order?.getTable(),
            payments: order?.getPayments() || [],
            client: order?.getClient(),
            internalNumberPrefix: order?.internal_number?.split('/')[0] || '',
            internalNumberSuffix: order?.internal_number?.split('/')[1] || '',
            isExport: order?.export || false,
            dueDate: order?.due_date || '',
            startDate: order?.start_date || '',
            endDate: order?.end_date || '',
            note: order?.note || '',
            flag: order?.flag || undefined,
            registerType: order?.register_type || undefined,
            taxPeriod: order?.tax_period || undefined,
            refNumber: order?.getFlag(OrderFlagType.ref)[0] || '',
            refTime: order?.getFlag(OrderFlagType.ref)[1] || '',
            pricelist: order?.getPricelist(),
            discountPercent: order?.getDiscountPercent(),
            curGuest: order?.getGuestId() || undefined,
            registerRequest: order?.isEinvoice()
                ? Order.getEinvoiceRegisterRequest(order)
                : undefined,
        };
    }
    static getEinvoiceRegisterRequest(order) {
        const orderRegisterRequest = order?.register_request;
        return {
            contract_number: orderRegisterRequest?.contract_number || '',
            invoice_number: orderRegisterRequest?.invoice_number || '',
            agreement_number: orderRegisterRequest?.agreement_number || '',
            receipt_number: orderRegisterRequest?.receipt_number || '',
            dispatch_number: orderRegisterRequest?.dispatch_number || '',
            project_number: orderRegisterRequest?.project_number || '',
            reference_number: orderRegisterRequest?.reference_number || '',
            reference_number_model: orderRegisterRequest?.reference_number_model || '',
            vat_obligation: orderRegisterRequest?.vat_obligation || 'by_delivery_date',
            due_date: orderRegisterRequest?.due_date || '',
            crf: orderRegisterRequest?.crf ?? !!order?.sendsToCrfByDefault(),
            attachments: orderRegisterRequest?.attachments || [],
            delivery_at: orderRegisterRequest?.delivery_at || moment().format('YYYY-MM-DD'),
        };
    }
    setPayOptions(options, shouldUpdateItems = false) {
        let { payments, internalNumberPrefix, internalNumberSuffix, status, client, table, registerType, registerRequest, flag, isExport, dueDate, startDate, endDate, note, taxPeriod, refNumber, refTime, curGuest, } = options;
        const existingZeroTaxMode = !!this.export || !!this.getClient()?.isDiplomat();
        this.status = status !== undefined ? status : this.status;
        this.due_date = dueDate !== undefined ? dueDate : this.due_date;
        this.start_date = startDate !== undefined ? startDate : this.start_date;
        this.end_date = endDate !== undefined ? endDate : this.end_date;
        this.tax_period = taxPeriod !== undefined ? taxPeriod : this.tax_period;
        this.note = note !== undefined ? note : this.note;
        this.register_type = registerType || this.register_type || this.determineRegisterType();
        this.table_id = table !== undefined ? (table?.id || null) : this.table_id;
        this.register_request = registerRequest || this.register_request;
        this.flag = flag !== undefined ? (flag ? this.mergeFlags(this.flag, flag) : null) : this.flag;
        this.export = isExport !== undefined ? isExport : this.export;
        this.client_id = client !== undefined ? (client?.id || null) : this.client_id;
        // A proforma never goes to SEF: an e-invoice type (and its request) inherited
        // from the order it was made from gives way to the proforma's own.
        if (this.isProforma() && this.isEinvoice()) {
            this.register_type = this.determineRegisterType();
            this.register_request = null;
        }
        this.internal_number = internalNumberPrefix && internalNumberSuffix
            ? `${internalNumberPrefix}/${internalNumberSuffix}`
            : (internalNumberPrefix || internalNumberSuffix || this.internal_number);
        if (refNumber && refTime)
            this.setFlag(OrderFlagType.ref, refNumber, refTime);
        else if (refNumber)
            this.setFlag(OrderFlagType.ref, refNumber);
        else
            this.removeFlag(OrderFlagType.ref);
        if (curGuest !== undefined) {
            this.setGuestId(curGuest);
        }
        if (shouldUpdateItems) {
            this.updateItems(options, existingZeroTaxMode);
        }
        if (payments)
            this.setPayments(payments);
    }
    updateItems(options, existingZeroTaxMode) {
        let { client, isExport, pricelist, discountPercent, } = options;
        let isUpdated = false;
        if (pricelist !== undefined && pricelist !== this.getPricelist()) {
            const result = this.setPricelist(pricelist);
            if (result.status !== 'failed') {
                isUpdated = true;
            }
        }
        if (discountPercent !== undefined && discountPercent !== this.getDiscountPercent()) {
            const result = this.giveDiscount(discountPercent);
            if (result.status !== 'failed') {
                isUpdated = true;
            }
        }
        const zeroTaxMode = !!isExport || !!client?.isDiplomat();
        if (zeroTaxMode) {
            this.setZeroItemsTax(client?.isDiplomat() ? 'diplomat' : 'export');
            isUpdated = true;
        }
        else if (existingZeroTaxMode) {
            this.setOriginalItemsTax();
            isUpdated = true;
        }
        if (!isUpdated)
            return { status: 'failed' };
        return { status: 'success', order: this };
    }
    close(options, shouldUpdateItems = false) {
        this.setPayOptions(options, shouldUpdateItems);
        // manually entered advance refund
        const hasReference = this.flag?.includes(OrderFlagType.ref);
        if (hasReference && this.isAdvance() && this.getToPay().toUnit() < 0 && !this.isImportedFromOldFiscalSystem()) {
            this.setFlag(OrderFlagType.type, 'closeAdvance');
        }
        const paidOrders = this.paysForOrders();
        this.taxes = !paidOrders.length ? Order.calculateTaxes(this) : Order.calculateTaxes(paidOrders);
        this.last_activity_at = moment().utc().format(Order.DATE_FORMAT);
        return this;
    }
    /**
     * Send receipt and slips to print
     */
    async sendToPrint(result, printOptions = {}, perfTracker) {
        if (result.status !== 'failed') {
            let delay = 0;
            if (!printOptions.skipPrintReceipt && this.shouldPrintReceipt()) {
                perfTracker?.startStep('print_receipt');
                delay = 2000;
                const printResult = await this.printReceipt(printOptions);
                perfTracker?.endStep(); // print_receipt
                if (!printResult.success) {
                    result.printError = I18n.t(`print.${printResult.error || 'receipt_printing_failed'}`);
                    result.status = 'successWithErrors';
                }
            }
            // Mirror the kitchen-ticket (KDS) issue gate below: a corrective /
            // cancellation document must not print kitchen slips either.
            if (!printOptions.skipPrintSlips && !this.correctsOthers() && this.shouldPrintSlips()) {
                setTimeout(() => this.printSlips(), delay);
            }
        }
        return result;
    }
    async issue(printOptions = {}, baseOrder, perfTracker) {
        const order = this.$duplicate();
        perfTracker?.startStep('order_validation');
        const validation = new OrderValidation(this, true, perfTracker, printOptions.cardTerminalApproved);
        const error = await validation.validate();
        if (error) {
            perfTracker?.endStep(); // order_validation
            return { status: 'failed', error };
        }
        perfTracker?.endStep(); // order_validation
        perfTracker?.startStep('order_before_issue');
        let beforeResult = await order.onBeforeIssue(perfTracker);
        perfTracker?.endStep(); // order_before_issue
        if (beforeResult) {
            if (beforeResult.order) {
                beforeResult = await beforeResult.order.sendToPrint(beforeResult, printOptions, perfTracker);
            }
            return beforeResult;
        }
        perfTracker?.startStep('order_sync');
        let result = await order.sync('create', perfTracker);
        perfTracker?.endStep(); // order_sync
        perfTracker?.startStep('order_after_issue');
        await order.onAfterIssue(printOptions, result, baseOrder);
        perfTracker?.endStep();
        perfTracker?.startStep('order_print');
        result = await order.sendToPrint(result, {
            ...printOptions,
            openDrawer: printOptions.openDrawer || order.isCashExchanged()
        }, perfTracker);
        perfTracker?.endStep(); // order_print
        return result;
    }
    async onBeforeIssue(perfTracker) {
        // A linked advance stays linked to the advance it was made from only as a refund of part of it: issued
        // at a negative amount and still naming that advance by its "Ref. broj".
        if (this.refers_to.some(order => order.reference?.type === 'linkedRefund')) {
            const named = this.getFlag(OrderFlagType.ref)[0];
            this.refers_to = this.refers_to.filter(order => order.reference?.type !== 'linkedRefund'
                || (this.getToPay().toUnit() < 0 && order.register_number === named));
        }
        this.number = this.shouldHaveNumber() ? await OrderService.getNextNumber() : null;
        this.last_activity_at = moment().utc().format(Order.DATE_FORMAT);
        // The fiscal IIC is derived from the number and last_activity_at stamped above;
        // one memoized earlier (a pre-issue receipt preview renders and caches it) belongs
        // to the old values. Drop it so the issued receipt's IIC comes from its final data.
        this.iic = null;
        if (Store.get('trainingMode')) {
            this.setFlag(OrderFlagType.mode, 'training');
        }
        // before order cancel
        if (this.correctsOthers()) {
            perfTracker?.startStep('handle_corrections');
            const correctType = this.getFlag(OrderFlagType.correctType)[0];
            if (correctType === 'cancel') {
                KitchenTicket.cancel(this.correctsOrders()[0]);
            }
            // mark corrected orders as canceled for FE reference. This is done also on BE
            // when the new corrective order is issued so no need to sync these except in case of
            // unpaid order which has no separate corrective order being issued
            const curUser = User.current();
            const ordersToSave = [];
            // This order holds a bare copy of each order it corrects (addReference): the copy is saved
            // without its empty references, so the stored order keeps its own (earlier corrections, an
            // advance's finals and closings).
            const stamp = (orders) => Order.insert({ data: orders.map(order => {
                    const record = order.$toJson();
                    delete record.refers_to;
                    delete record.referred_by;
                    return record;
                }) });
            for (const order of this.correctsOrders()) {
                order.canceled_by_user_id = curUser?.id || null;
                order.canceled_at = moment().utc().format(Order.DATE_FORMAT);
                if (this.correctsPartly())
                    order.setFlag(OrderFlagType.corrected, 'partial');
                else
                    order.removeFlag(OrderFlagType.corrected);
                if (order?.isUnpaid() && !I18n.c('order.unpaidIssuesReceipt')) {
                    stamp(ordersToSave);
                    perfTracker?.endStep(); // handle_corrections
                    return order.sync('cancel');
                }
                ordersToSave.push(order);
            }
            stamp(ordersToSave);
            perfTracker?.endStep(); // handle_corrections
        }
        return undefined;
    }
    async onAfterIssue(printOptions, result, baseOrder) {
        if (result.status !== 'failed') {
            if (this.number) {
                OrderService.setLatestNumber(this.number);
            }
            // A proforma's number ("Broj predračuna") is its own: it never moves the invoice sequence.
            if (this.internal_number && !this.isProforma()) {
                OrderService.setLatestInternalNumber(this.internal_number);
            }
            // if this order is cancelling the baseOrder which itself is a refund,
            // then reverse baseOrder's refunded orders - unset canceled_at
            const isRefund = ['cancel', 'cancelSummary'].includes(this.getFlag(OrderFlagType.correctType)[0]) || this.getFlag(OrderFlagType.type)[0] === 'closeAdvance';
            const isBaseOrderCorrected = isRefund && baseOrder && this.correctsOrders().some(o => o.hash === baseOrder.hash);
            if (isBaseOrderCorrected) {
                const reversedOrders = baseOrder.correctsOrders();
                for (const order of reversedOrders) {
                    order.canceled_by_user_id = null;
                    order.canceled_at = null;
                    order.removeFlag(OrderFlagType.corrected);
                }
                Order.insert({ data: reversedOrders.map(o => o.$toJson()) });
            }
            // Only a final that was issued used its advance; a failed one was neither registered nor stored.
            if (this.status === OrderStatusType.paid) {
                this.closeReferredAdvanceOrders();
            }
            // An advance used in parts: a cancelled final gives back what it used — its closing
            // is undone right after the final's storno.
            if (I18n.c('order.advanceInParts')) {
                const reopenError = await AdvanceFlowService.reopen(this);
                if (reopenError) {
                    result.status = 'successWithErrors';
                    result.error = reopenError;
                }
            }
        }
        // Gate on a successful, non-corrective issue. On a failed
        // fiscal registration the slips don't print, so creating the KDS
        // ticket anyway makes ticket and paper diverge — and a retry of the
        // same cart spawns a second ticket with the same order_hash that
        // cancel/deleteItem use .find() on, leaving it uncancellable. A
        // corrective/storno order already cancels the original ticket in
        // onBeforeIssue; letting it also create one produces a phantom
        // negated-count ticket ("-2 x Burger") on the KDS in
        // unpaidIssuesReceipt markets (ME).
        if (result.status !== 'failed' && !this.correctsOthers() && !printOptions.skipPrintSlips && this.shouldPrintSlips()) {
            KitchenTicket.issue(this);
        }
        // FE-driven TableSession auto-open (the close check is anchored to
        // sync settlement inside sync() — see handleAutoClose). Awaited so
        // the PIN modal downstream can read result.table_session, but capped
        // at 2.5s like Order.sync's own race: on slow networks we fall back
        // to the locally persisted session and let the sync finish behind.
        if (result.status !== 'failed' && this.status === OrderStatusType.unpaid && this.table_id) {
            // Captured before the race: the timeout fallback must only surface
            // a session this call actually opened — a pre-existing one would
            // pop the PIN modal on every order.
            const hadSession = !!TableSession.forTable(this.table_id);
            let timeoutId;
            const timeout = new Promise(resolve => { timeoutId = setTimeout(() => resolve(null), 2500); });
            const opened = await Promise.race([TableSessionService.handleAutoOpen(this), timeout]);
            if (timeoutId)
                clearTimeout(timeoutId);
            const openedSession = opened
                ? opened.openedSession
                : hadSession ? null : TableSession.forTable(this.table_id) || null;
            if (openedSession) {
                result.table_session = openedSession.$toJson();
            }
        }
    }
    async prepareCopy(options = {}) {
        const order = (await this.fresh(true)).addReference(this, 'copy').close({
            ...options,
            status: OrderStatusType.copy
        });
        order.flag = this.flag;
        order.taxes = this.taxes;
        return order;
    }
    static async prepareSummary(orders, options = {}) {
        orders = orders.map(o => o.$duplicate());
        const allowSummary = I18n.c('order.allowReceiptSummary');
        const summaryOrder = await Order.createNew();
        const alreadyHaveSummaryReference = orders[0].referred_by.some(order => order.reference?.type === 'summary');
        if (!alreadyHaveSummaryReference) {
            summaryOrder.hash = orders[0].hash.slice(5) + orders[0].hash.slice(0, 5);
        }
        const firstClientId = orders.find(order => !!order.client_id)?.client_id || 0;
        if (firstClientId)
            summaryOrder.client_id = firstClientId;
        const firstTableId = orders.find(order => !!order.table_id)?.table_id || 0;
        const allTableIdsSame = orders.every(order => order.table_id === firstTableId);
        if (allTableIdsSame)
            summaryOrder.table_id = firstTableId;
        if (allowSummary) {
            summaryOrder.items = [];
            for (const o of orders) {
                summaryOrder.items.push(await OrderItem.createForOrder(summaryOrder, o));
            }
        }
        else {
            summaryOrder.items = OrderItem.merge(orders.flatMap(order => order.items), summaryOrder);
        }
        orders.forEach(order => summaryOrder.addReference(order, 'summary'));
        summaryOrder.close({
            ...options,
            status: allowSummary ? OrderStatusType.summary : OrderStatusType.paid
        });
        return summaryOrder;
    }
    async prepareCorrect(correctedItems, options = {}) {
        const order = await Order.createNew();
        order.table_id = this.table_id;
        order.client_id = this.client_id;
        order.export = this.export;
        order.register_type = this.register_type;
        const noCompensation = this.getFlag(OrderFlagType.type).includes('noCompensation');
        if (noCompensation)
            order.setFlag(OrderFlagType.type, 'noCompensation');
        order.items = correctedItems.map(orderItem => orderItem.fresh(order));
        const paymentType = CashRegister.availablePaymentTypes().find(payment => Money.parse(this[`paid_${payment.key}`]).toUnit() > 0);
        if (paymentType) {
            const payments = [{ key: paymentType.key, amount: order.getLeftToPay().toRoundedUnit(4) }];
            options.payments = payments;
        }
        order.addReference(this, 'corrective');
        order.setFlag(OrderFlagType.correctType, this.paysForOrders().length > 0 ? 'correctSummary' : 'correct');
        order.close({
            ...options,
            status: this.status
        });
        return order;
    }
    async prepareCancel(options = {}, clearOriginalUserDebt = false) {
        const order = await Order.createNew();
        if (clearOriginalUserDebt) {
            order.setFlag(OrderFlagType.clearDebtForUser, this.user_id + '');
        }
        order.table_id = this.table_id;
        order.client_id = this.client_id;
        order.export = this.export;
        order.register_type = this.register_type;
        order.source = this.source;
        // External documents never register — mirror the marker the server
        // stamps on the external original it creates.
        if (this.isExternal())
            order.register_status = OrderRegisterStatusType.skipped;
        const noCompensation = this.getFlag(OrderFlagType.type).includes('noCompensation');
        if (noCompensation)
            order.setFlag(OrderFlagType.type, 'noCompensation');
        // A Na rate storno takes its Ostalo share back off the buyer's installment debt (the same mark adds it).
        if (this.getFlag(OrderFlagType.type).includes('installments'))
            order.setFlag(OrderFlagType.type, 'installments');
        const paidAdvance = Money.parse(this.getFlag(OrderFlagType.advance)[0] || 0);
        if (paidAdvance.toUnit() !== 0) {
            order.setFlag(OrderFlagType.advance, paidAdvance.multiply(-1).toFormat('0.0000'));
        }
        order.items = this.items.map(orderItem => orderItem.fresh(order, true));
        order.paid_cash = this.getPaidCash().multiply(-1).toFormat('0.0000');
        order.paid_credit_card = this.getPaidCreditCard().multiply(-1).toFormat('0.0000');
        order.paid_invoice = this.getPaidInvoice().multiply(-1).toFormat('0.0000');
        order.paid_credit_card_business = this.getPaidCreditCardBusiness().multiply(-1).toFormat('0.0000');
        order.paid_advance = this.getPaidAdvance().multiply(-1).toFormat('0.0000');
        order.paid_check = this.getPaidCheck().multiply(-1).toFormat('0.0000');
        order.paid_voucher = this.getPaidVoucher().multiply(-1).toFormat('0.0000');
        order.paid_mobile_money = this.getPaidMobileMoney().multiply(-1).toFormat('0.0000');
        order.paid_other_noncash = this.getPaidOtherNoncash().multiply(-1).toFormat('0.0000');
        order.tax_period = this.tax_period;
        order.addReference(this, 'corrective');
        order.setFlag(OrderFlagType.correctType, this.paysForOrders().length > 0 ? 'cancelSummary' : 'cancel');
        order.close({
            ...options,
            status: this.status
        });
        return order;
    }
    async prepareCancelSummary(options = {}, clearOriginalUserDebt = false) {
        const order = await Order.createNew();
        if (clearOriginalUserDebt) {
            order.setFlag(OrderFlagType.clearDebtForUser, this.user_id + '');
        }
        order.table_id = this.table_id;
        order.client_id = this.client_id;
        order.export = this.export;
        order.register_type = this.register_type;
        const noCompensation = this.getFlag(OrderFlagType.type).includes('noCompensation');
        if (noCompensation)
            order.setFlag(OrderFlagType.type, 'noCompensation');
        // A Na rate storno takes its Ostalo share back off the buyer's installment debt (the same mark adds it).
        if (this.getFlag(OrderFlagType.type).includes('installments'))
            order.setFlag(OrderFlagType.type, 'installments');
        order.items = this.items.map(orderItem => orderItem.fresh(order, true));
        order.paid_cash = this.getPaidCash().multiply(-1).toFormat('0.0000');
        order.paid_credit_card = this.getPaidCreditCard().multiply(-1).toFormat('0.0000');
        order.paid_invoice = this.getPaidInvoice().multiply(-1).toFormat('0.0000');
        order.paid_credit_card_business = this.getPaidCreditCardBusiness().multiply(-1).toFormat('0.0000');
        order.paid_advance = this.getPaidAdvance().multiply(-1).toFormat('0.0000');
        order.paid_check = this.getPaidCheck().multiply(-1).toFormat('0.0000');
        order.paid_voucher = this.getPaidVoucher().multiply(-1).toFormat('0.0000');
        order.paid_mobile_money = this.getPaidMobileMoney().multiply(-1).toFormat('0.0000');
        order.paid_other_noncash = this.getPaidOtherNoncash().multiply(-1).toFormat('0.0000');
        order.tax_period = this.tax_period;
        order.addReference(this, 'corrective');
        order.setFlag(OrderFlagType.correctType, this.paysForOrders().length > 0 ? 'cancelSummary' : 'cancel');
        this.paysForOrders().forEach(o => order.addReference(o, 'summary'));
        order.close({
            ...options,
            status: this.status
        });
        order.taxes = order.taxes.map(t => {
            t.price_before_tax = Money.parse(t.price_before_tax).multiply(-1).toFormat('0.0000');
            t.value = Money.parse(t.value).multiply(-1).toFormat('0.0000');
            return t;
        });
        return order;
    }
    static async prepareCreditNote(orders, items, options = {}) {
        const { flag } = options;
        let order = await Order.createNew();
        // The batch selection enforces one common buyer across the sources — carry it over
        // (the credit-note form passes no client; an explicit options.client still wins in close()).
        order.client_id = orders.find(o => !!o.client_id)?.client_id ?? null;
        // The receipts this credit note credits: the backend builds its fiscal references
        // (me: IICRefs) from this list; only a subsequent delivery derives them from the items.
        order.credit_note_referrals = orders.map(source => source.hash);
        order.items = items.map(orderItem => {
            return new OrderItem({
                ...orderItem.$toJson(),
                ...{
                    id: null,
                    hash: HashGen.long(),
                    order_id: null,
                    order_hash: order.hash,
                    ref_order_hash: !flag?.includes('summaryCreditNote') ? orderItem.order_hash : null,
                    ref_order_item_hash: !flag?.includes('summaryCreditNote') ? orderItem.hash : null
                }
            });
        });
        if (Order.fiscalStatuses().includes(OrderStatusType.creditNote)) {
            order.register_type = OrderRegisterType.fiscal;
        }
        else {
            order.register_type = !EinvoiceService.einvoiceOnly()
                ? OrderRegisterType.manual
                : orders[0].register_type;
        }
        order.close({
            ...options,
            status: OrderStatusType.creditNote
        });
        return order;
    }
    async prepareProformaFinal(options = {}) {
        return (await this.fresh(true)).addReference(this, 'proforma').close({
            ...options,
            status: OrderStatusType.paid
        });
    }
    /**
     * Close advance receipts with one refund over them (Serbia). `linkedRefunds` holds, by advance hash,
     * the live linked refunds that already took part of an advance (a linked advance at a negative amount,
     * linked to its advance, or one issued before refunds kept the link, named only by its "Ref. broj"): the
     * refund takes only what they left of it, paid back by the advance's own methods. An advance without one
     * is closed in full, line for line.
     */
    static async prepareAdvanceCancel(orders, options = {}, linkedRefunds = new Map()) {
        if (!options.client)
            options.client = orders.find(o => !!o.client_id)?.getClient();
        let { client, isExport = false } = options;
        const order = await Order.createNew();
        order.export = isExport || false;
        order.client_id = client?.id || null;
        order.setFlag(OrderFlagType.type, 'closeAdvance');
        const refundsOf = (advance) => linkedRefunds.get(advance.hash) ?? [];
        order.items = orders.flatMap(o => refundsOf(o).length
            ? Order.advanceRestLines(o, refundsOf(o), order)
            : o.items.map(item => item.fresh(order, true)));
        const paidBack = orders.map(o => Order.advancePaidBack(o, refundsOf(o)));
        for (const key of Object.values(PaymentTypeLabel)) {
            order[`paid_${key}`] = paidBack.reduce((acc, cur) => acc.add(cur[key]), Money.parse(0)).multiply(-1).toFormat('0.0000');
        }
        orders.sort((o1, o2) => {
            if (o1.last_activity_at === null || o2.last_activity_at === null)
                return 0;
            return new Date(o2.last_activity_at).getTime() - new Date(o1.last_activity_at).getTime();
        }).forEach(o => order.addReference(o, 'corrective'));
        order.close({
            ...options,
            status: OrderStatusType.advance
        });
        return order;
    }
    /**
     * The closing lines of an advance its linked refunds took part of: what they left at each tax rate, on
     * the advance's own (first) line at that rate — quantity −1, priced with VAT, no discount.
     */
    static advanceRestLines(advance, refunds, closing) {
        const rateOf = (item) => String(item.tax_rate_id ?? item.tax_rate);
        const left = new Map();
        for (const item of [...advance.items, ...refunds.flatMap(refund => refund.items)]) {
            left.set(rateOf(item), (left.get(rateOf(item)) ?? Money.parse(0)).add(item.getToPay()));
        }
        // A refund larger than its rate's share of the advance (or at a rate the advance doesn't have) takes the
        // difference off the advance's other rates, in line order: the lines still add up to what is left.
        let over = [...left.values()].reduce((acc, amount) => amount.toUnit() < 0 ? acc.subtract(amount) : acc, Money.parse(0));
        return advance.items.flatMap(item => {
            let amount = left.get(rateOf(item));
            left.delete(rateOf(item));
            if (!amount || amount.toUnit() <= 0)
                return [];
            const taken = over.toUnit() < amount.toUnit() ? over : amount;
            amount = amount.subtract(taken);
            over = over.subtract(taken);
            if (amount.toUnit() <= 0)
                return [];
            const line = item.fresh(closing, true);
            line.count = '-1.000';
            line.unit_price = amount.toFormat('0.0000');
            line.discount_percent = '0.00';
            line.additional_count = '0.000';
            line.additional_discount_percent = '0.00';
            line.reverse_calculation = false;
            return [line];
        });
    }
    /**
     * What an advance pays back, by method: all it was paid, or — when linked refunds took part of it — what is
     * left, taken from its own methods in turn (a refund may have gone out by another method).
     */
    static advancePaidBack(advance, refunds) {
        let rest = refunds.reduce((left, refund) => left.add(refund.getToPay()), advance.getToPay());
        return Object.fromEntries(Object.values(PaymentTypeLabel).map(key => {
            const paid = Money.parse(advance[`paid_${key}`]);
            if (!refunds.length)
                return [key, paid];
            const taken = paid.toUnit() <= 0 ? Money.parse(0) : paid.toUnit() < rest.toUnit() ? paid : rest;
            rest = rest.subtract(taken);
            return [key, taken];
        }));
    }
    static async prepareAdvanceCorrect(orders, orderFromAdvance, options = {}) {
        let { client, isExport = false } = options;
        if (!client)
            client = orders.find(o => !!o.client_id)?.getClient();
        const order = await Order.createNew();
        order.export = isExport;
        order.client_id = client?.id || null;
        order.setFlag(OrderFlagType.type, 'closeAdvance', 'correct');
        order.items = [OrderItem.createForAdvanceItem(order, orderFromAdvance.taxes[0], orderFromAdvance.paid_advance)];
        const advanceRefOrderPaymentType = CashRegister.availablePaymentTypes().find(payment => Money.parse(orders[0][`paid_${payment.key}`]).toUnit() > 0);
        options.payments = [{
                key: advanceRefOrderPaymentType.key,
                amount: order.getLeftToPay().toRoundedUnit(4)
            }];
        orders.sort((o1, o2) => {
            if (o1.last_activity_at === null || o2.last_activity_at === null)
                return 0;
            return new Date(o2.last_activity_at).getTime() - new Date(o1.last_activity_at).getTime();
        }).forEach(advance => order.addReference(advance, 'corrective'));
        order.close({
            ...options,
            status: OrderStatusType.advance
        });
        return order;
    }
    /**
     * Create an E-Invoice for this receipt and cancel it afterwards.
     */
    async einvoiceFromFiscal(options) {
        let { registerRequest } = options;
        if (!connectionService.isOnline) {
            return {
                status: 'failed',
                error: I18n.t('connection.internet_offline_message_not_allowed')
            };
        }
        await fiscalService.refreshSdcStatus();
        if (!fiscalService.sdcStatus || fiscalService.sdcStatus.error) {
            return {
                status: 'failed',
                error: !fiscalService.sdcStatus?.error ? I18n.t('sdc.status.not_initialized') : fiscalService.sdcStatus.error.message
            };
        }
        const freshOrder = await this.fresh();
        freshOrder.addReference(this, 'einvoice');
        const result = await freshOrder.close({
            status: this.status,
            payments: [{
                    key: PaymentTypeLabel.invoice,
                    amount: freshOrder.getLeftToPay().toRoundedUnit(4)
                }],
            registerType: OrderRegisterType.einvoice,
            registerRequest,
            dueDate: registerRequest?.due_date ? moment(registerRequest?.due_date).format(Order.DATE_FORMAT) : undefined
        }).issue({ forceA4Format: true });
        // A failed print of the e-invoice ('successWithErrors') does not undo its upload: the receipt it
        // replaces is cancelled all the same.
        if (result.status !== 'failed') {
            const cancelResult = await (await this.prepareCancel()).issue({ skipPrintSlips: true });
            if (cancelResult.status === 'failed') {
                return {
                    status: 'successWithErrors',
                    error: I18n.t('einvoice.fiscal_cancel_needed')
                };
            }
            for (const order of this.paysForOrders()) {
                await (await order.prepareCancel()).issue({ skipPrintSlips: true });
            }
        }
        return result;
    }
    /**
     * Close non-closed advance orders that this order refers to
     */
    async closeReferredAdvanceOrders() {
        if (I18n.c('order.advanceInParts'))
            return AdvanceFlowService.close(this);
        const remainingAdvance = this.getAdvanceRest();
        if (!remainingAdvance)
            return;
        const advanceRefOrders = this.referredAdvanceOrders();
        const trainingModeMismatch = advanceRefOrders.some(order => !!Store.get('trainingMode') !== !!order.isTraining());
        if (trainingModeMismatch)
            return;
        if (remainingAdvance.toUnit() <= 0) {
            for (let i = 0; i < advanceRefOrders.length; i++) {
                await (await advanceRefOrders[i].prepareCancel()).issue({ skipPrintSlips: true });
            }
            return;
        }
        // The closing stamps the advance receipts before it registers (onBeforeIssue). When the
        // registration fails (e.g. the Bosnian fiscal device refuses it) nothing closed them, so they
        // get back the stamp they had — an earlier closing's included — and stay open for the rest.
        const stamps = advanceRefOrders.map(advance => {
            const { hash, canceled_at, canceled_by_user_id } = Order.find(advance.hash) ?? advance;
            return { hash, canceled_at, canceled_by_user_id };
        });
        const order = await Order.prepareAdvanceCorrect(advanceRefOrders, this, { client: this.getClient() });
        if ((await order.issue()).status === 'failed') {
            Order.update(stamps);
        }
    }
    /** The advance this final uses, as stored — a final's own copy of it carries no later closings. */
    storedAdvance() {
        const advance = this.referredAdvanceOrders()[0];
        return Order.find(advance.hash) ?? advance;
    }
    /**
     * A closing whose final(s) were cancelled but whose undo did not go through (advances used in parts) —
     * the manual retry of Route 1.
     */
    allowAdvanceClosingUndo() {
        if (!I18n.c('order.advanceInParts') || !this.isAdvance() || this.canceled())
            return false;
        if (!CashRegister.current() || !Shift.current())
            return false;
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        const finals = this.refers_to
            .filter(order => order.reference?.type === 'advanceFinal')
            .map(order => Order.find(order.hash) ?? order);
        return finals.length > 0
            && finals.every(final => final.canceled() && final.getFlag(OrderFlagType.corrected)[0] !== 'partial');
    }
    /**
     * What this final leaves of the advance receipts the POS closes for it, measured on its own copies
     * of them (they carry no earlier closings): above zero they are closed in part, otherwise in full.
     * Null when the POS closes none: not a final from an advance, an e-invoice advance (the server
     * closes it), or a final over an advance refund.
     */
    getAdvanceRest() {
        if (!this.refersToAdvance() || !this.isPaid())
            return null;
        const advanceRefOrders = this.referredAdvanceOrders();
        if (advanceRefOrders[0].isEinvoice() || advanceRefOrders[0].getToPay().toUnit() < 0)
            return null;
        return advanceRefOrders
            .reduce((acc, cur) => acc.add(cur.getRemainingAdvanceAmount().abs()), Money.parse(0))
            .subtract(this.getPaidAdvanceEffective());
    }
    /**
     * This final used only part of its advance, and a partial closing stands on it: the advance is
     * stamped — in the issuing terminal's store at once, in the copy the server sends once the closing
     * lands. Unless the closing names this final (advances used in parts — cancelBlockedByAdvance), it
     * can't be undone, so cancelling the final would not give the part back. The stamp is per advance:
     * a final whose own closing never went out (issued before partial closings did) is held too once a
     * later final closes the same advance.
     */
    closedAdvancePartly() {
        const rest = this.getAdvanceRest();
        if (!rest || rest.toUnit() <= 0)
            return false;
        return this.referredAdvanceOrders().some(advance => advance.canceled() || !!Order.find(advance.hash)?.canceled());
    }
    /**
     * A partial closing that stands on this final blocks its cancel — except, for advances used in parts, when
     * the closing names this final (`advanceFinal`): cancelling then undoes the closing (Route 1).
     * A final whose closing names nothing (issued before closings named their finals) keeps the block.
     */
    cancelBlockedByAdvance() {
        if (!this.closedAdvancePartly())
            return false;
        if (!I18n.c('order.advanceInParts'))
            return true;
        const stored = Order.find(this.hash) ?? this;
        return !stored.referred_by.some(order => order.reference?.type === 'advanceFinal');
    }
    /**
     * Add reference to the other order either by making
     * this order refer to the added one or vice versa.
     */
    addReference(order, referenceType, referredByOther = false) {
        const refOrder = order.$duplicate();
        // References carry one level only — the embedded copy must never
        // bring its own references along.
        refOrder.refers_to = [];
        refOrder.referred_by = [];
        refOrder.reference = {
            referred_to_order_hash: !referredByOther ? refOrder.hash : this.hash,
            referred_by_order_hash: !referredByOther ? this.hash : refOrder.hash,
            type: referenceType
        };
        if (!referredByOther) {
            this.refers_to.push(refOrder);
        }
        else {
            this.referred_by.push(refOrder);
        }
        return this;
    }
    /**
     * Give order discount. Will persist the changes to the store, and sync
     * with the server if the order is not new / has previously been issued.
     */
    giveDiscount(percent) {
        if (percent < 0 || percent > 99 || this.isSummary())
            return { status: 'failed' };
        const uniqueDiscounts = this.items
            .map(item => item.getDiscountPercent())
            .filter((value, index, array) => index === array.indexOf(value));
        if (uniqueDiscounts.length > 1 || uniqueDiscounts[0] === percent)
            return { status: 'failed' };
        if (percent) {
            this.setFlag(OrderFlagType.discountPercent, percent + '');
        }
        else {
            this.removeFlag(OrderFlagType.discountPercent);
        }
        this.items.forEach(orderItem => orderItem.giveDiscount(percent));
        this.taxes = Order.calculateTaxes(this);
        return { status: 'success', order: this };
    }
    getDiscountPercent() {
        const discountPercent = this.getFlag(OrderFlagType.discountPercent);
        if (!discountPercent.length)
            return undefined;
        return this.round(discountPercent[0], 2);
    }
    /**
     * Update item count
     */
    async updateItemCount(item, count, sync = false, skipPrintSlips = false) {
        if (count > 999999 || this.isSummary())
            return { status: 'failed' };
        const originalOrder = this.$duplicate();
        if (count == 0) {
            return this.deleteItem(item, sync, skipPrintSlips);
        }
        const orderItem = this.items.find(orderItem => orderItem.hash === item.hash);
        if (orderItem) {
            orderItem.updateCount(count);
            if (orderItem.getToPay().toFormat('0.00') === '0.00') {
                return this.deleteItem(item, sync, skipPrintSlips);
            }
        }
        this.taxes = Order.calculateTaxes(this);
        if (sync) {
            if (this.isFiscal()) {
                const syncResult = await originalOrder.replaceUnpaidOrder(await this.fresh(true));
                if (syncResult.order && !skipPrintSlips)
                    KitchenTicket.replaceOrder(originalOrder, syncResult.order);
                return syncResult;
            }
            else if (this.isIssued()) {
                const syncResult = await this.sync('update');
                if (syncResult.status !== 'failed' && !skipPrintSlips)
                    KitchenTicket.updateItemCount(this, item);
                return syncResult;
            }
        }
        return { status: 'success', order: this };
    }
    /**
     * Update item unit price
     */
    updateItemUnitPrice(item, price) {
        if (price <= 0 || price > I18n.c('order.unitPriceLimit'))
            return;
        if (this.isSummary())
            return;
        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.updateUnitPrice(price);
            }
        });
        this.taxes = Order.calculateTaxes(this);
    }
    /**
     * Give item discount
     */
    async giveItemDiscount(item, percent, sync = false) {
        if (percent < 0 || percent > 99 || this.isSummary())
            return { status: 'failed' };
        const originalOrder = this.$duplicate();
        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.giveDiscount(percent);
            }
        });
        const uniqueDiscounts = this.items
            .map(item => item.getDiscountPercent())
            .filter((value, index, array) => index === array.indexOf(value));
        if (uniqueDiscounts.length > 1 || uniqueDiscounts[0] !== percent) {
            this.removeFlag(OrderFlagType.discountPercent);
        }
        this.taxes = Order.calculateTaxes(this);
        if (sync) {
            if (this.isFiscal()) {
                const syncResult = await originalOrder.replaceUnpaidOrder(await this.fresh(true));
                if (syncResult.order) {
                    // Re-point the KDS ticket to the replacement order (audit M5;
                    // mirrors deleteItem's replaceUnpaidOrder path).
                    KitchenTicket.replaceOrder(originalOrder, syncResult.order);
                }
                return syncResult;
            }
            else if (this.isIssued()) {
                return this.sync('update');
            }
        }
        return { status: 'success', order: this };
    }
    /**
     * Set the pricelist for the order
     */
    setPricelist(pricelist = 0) {
        if (this.isSummary())
            return { status: 'failed' };
        const existingPricelist = this.getPricelist();
        if (existingPricelist === pricelist)
            return { status: 'failed' };
        if (pricelist) {
            this.setFlag(OrderFlagType.pricelist, pricelist + '');
        }
        else {
            this.removeFlag(OrderFlagType.pricelist);
        }
        this.items.forEach(orderItem => orderItem.setPricelist(this, pricelist));
        this.taxes = Order.calculateTaxes(this);
        return { status: 'success', order: this };
    }
    /**
     * Get the pricelist of the order
     */
    getPricelist() {
        const pricelist = this.getFlag(OrderFlagType.pricelist);
        if (!pricelist.length)
            return 0;
        return parseInt(pricelist[0]);
    }
    /**
     * Add order item. If the order already contains new / not yet issued item
     * for the same product, update it. Otherwise create a new item.
     */
    addItem(product, count, discountPercent = 0, pricelist = 0) {
        if (count && count > 999999)
            return null;
        if (product.getSellingPrice().toUnit() < 0)
            return null;
        if (this.isSummary())
            return null;
        let countToAdd = count || (!product.is_made ? parseFloat(product.selling_count) : 1);
        if (countToAdd <= 0)
            return null;
        const newOrderItem = OrderItem.createForProduct(this, product, countToAdd, discountPercent, pricelist);
        const forceSeparate = Store.get('tenant')?.venue?.settings?.item_force_separate;
        const separators = this.getItemSeparators();
        let matchedItems = this.items.filter((item, index) => {
            const isInLastSection = separators.length === 0 || +separators[separators.length - 1] <= index;
            return isInLastSection && item.product_id === newOrderItem.product_id && item.getDiscountPercent() === newOrderItem.getDiscountPercent();
        });
        let itemToUpdate = !forceSeparate && matchedItems.length > 0 ? matchedItems[matchedItems.length - 1] : undefined;
        if (itemToUpdate) {
            // move item to the last position in the array
            this.items.push(this.items.splice(this.items.indexOf(itemToUpdate), 1)[0]);
            itemToUpdate.updateCount(parseFloat(itemToUpdate.count) + countToAdd);
            this.taxes = Order.calculateTaxes(this);
            return itemToUpdate;
        }
        this.items.push(newOrderItem);
        this.taxes = Order.calculateTaxes(this);
        return newOrderItem;
    }
    /**
     * Delete OrderItem
     */
    async deleteItem(item, sync = false, skipPrintSlips = false) {
        if (this.isSummary())
            return { status: 'failed' };
        const originalOrder = this.$duplicate();
        this.items = this.items.filter(orderItem => orderItem.hash !== item.hash);
        this.taxes = Order.calculateTaxes(this);
        const isEmpty = !this.items.length;
        let syncResult = null;
        let isSuccess = true;
        let updatedOrder = this;
        if (sync) {
            if (this.isFiscal()) {
                if (!isEmpty) {
                    syncResult = await originalOrder.replaceUnpaidOrder(await this.fresh(true));
                    if (syncResult.order && !skipPrintSlips) {
                        KitchenTicket.replaceOrder(originalOrder, syncResult.order);
                        updatedOrder = syncResult.order;
                    }
                }
                else {
                    syncResult = await (await originalOrder.prepareCancel()).issue({ skipPrintReceipt: true, skipPrintSlips: true });
                    isSuccess = syncResult.status !== 'failed';
                    if (isSuccess && !skipPrintSlips)
                        KitchenTicket.deleteItem(this, item);
                }
                isSuccess = syncResult.status !== 'failed';
            }
            else if (this.isIssued()) {
                const action = isEmpty ? 'delete' : 'update';
                syncResult = await this.sync(action);
                isSuccess = syncResult.status !== 'failed';
                if (isSuccess && !skipPrintSlips)
                    KitchenTicket.deleteItem(this, item);
            }
        }
        if (isSuccess) {
            const shouldPrintSlips = this.isIssued() && this.shouldPrintSlips();
            if (!skipPrintSlips && shouldPrintSlips) {
                const orderForPrint = originalOrder.$duplicate();
                orderForPrint.items = orderForPrint.items
                    .filter(orderItem => orderItem.hash === item.hash)
                    .map(orderItem => {
                    orderItem.count = (-1 * orderItem.getCount()).toFixed(3);
                    return orderItem;
                });
                orderForPrint.printSlips();
            }
            if (isEmpty) {
                const wasIssued = this.isIssued();
                if (wasIssued) {
                    Order.syncTerminals({ items: [{ action: 'delete', record: this.$toJson() }] });
                }
                this.$delete();
                // Removing the last item of an issued tab empties the table —
                // run the close check (force: a deleted order has no
                // canceled_at/paid status to match the standard gate).
                // Never-issued orders were never on the table.
                if (wasIssued) {
                    TableSessionService.handleAutoClose(this, true).catch(() => { });
                }
            }
        }
        return {
            status: isSuccess ? 'success' : 'failed',
            order: isSuccess && !isEmpty ? updatedOrder : undefined
        };
    }
    async replaceUnpaidOrder(order) {
        if (this.status !== OrderStatusType.unpaid)
            return { status: 'failed' };
        const syncResult = await order.close({
            refNumber: this.hash,
            refTime: this.getFlag(OrderFlagType.ref)[1] || this.getLastActivityAt()?.format('YYYY-MM-DD HH:mm:ss') || '',
            status: OrderStatusType.unpaid,
            payments: [],
            flag: `${OrderFlagType.type},${CustomPaymentTypeLabel.order}`
        }).issue({ skipPrintReceipt: true, skipPrintSlips: true });
        if (syncResult.status === 'failed')
            return syncResult;
        const cancelResult = await (await this.prepareCancel()).issue({ skipPrintReceipt: true, skipPrintSlips: true });
        if (cancelResult.status === 'failed') {
            return { status: 'failed', error: cancelResult.error };
        }
        return syncResult;
    }
    /**
     * Exempt item from tax
     */
    setItemExemptFromTax(item, exemptFromTaxReason) {
        if (this.isSummary())
            return;
        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.setExemptFromTax(exemptFromTaxReason);
            }
        });
        this.taxes = Order.calculateTaxes(this);
    }
    /**
     * Set Item Comments
     */
    setItemComments(item, comments) {
        if (this.isSummary())
            return;
        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.setComments(comments);
            }
        });
    }
    /**
     * Get IsIncoming
     */
    isIncoming() {
        return !!this.incoming;
    }
    /**
     * Get IsEinvoice
     */
    isEinvoice() {
        return this.register_type === OrderRegisterType.einvoice;
    }
    /**
     * Whether "Pošalji u CRF" (register in the Central Invoice Register) starts ticked:
     * a regular invoice to a public-funds buyer (JBKJS) — new, paid or summary (a new
     * order is issued as paid), never corrective. CRF does not register advance
     * invoices or credit notes.
     */
    sendsToCrfByDefault() {
        const regularStatuses = [OrderStatusType.new, OrderStatusType.paid, OrderStatusType.summary];
        return this.getClient()?.id_type === ClientIDType.tax_id_number_local_jbkjs
            && regularStatuses.includes(this.status)
            && !this.correctsOthers();
    }
    /**
     * Check if order is fiscal
     */
    isFiscal() {
        return this.register_type === OrderRegisterType.fiscal;
    }
    isFiscalRegistered() {
        return this.isFiscal()
            && (this.register_status === OrderRegisterStatusType.success || this.register_status === OrderRegisterStatusType.successSubsequent);
    }
    /**
     * Issued = the server has assigned an id, or the order is in the model
     * store. A tab issued offline has no id until the sync queue replays, so
     * `id` alone must never gate "was this issued"; an issued tab that a peer
     * has meanwhile paid or removed may be gone from the store while the open
     * table still lists it, so store membership alone must not gate it either
     * — its edit has to reach the server and fail there, visibly.
     */
    isIssued() {
        return !!this.id || !!Order.find(this.hash);
    }
    /**
     * Get IsExternal
     */
    isExternal() {
        return this.register_type === OrderRegisterType.external;
    }
    /**
     * RULE predicate — "is somebody else settling this bill?" Stamped by
     * whichever external system holds it (`settled_by`, any provider), on the
     * charge and on the corrective that reverses it alike. Every guard keys on
     * this: paying, summarizing or crediting such an order locally would charge
     * the guest twice, and it may only be cancelled in full. Provider-neutral
     * on purpose — a second settlement integration must be caught here too.
     */
    isSettledExternally() {
        return !!this.settled_by;
    }
    /**
     * The receipt whose bill an external system holds — the charge, never the
     * corrective that reverses it (the server stamps `settled_by` on those too,
     * as the reversal marker the storno-retry flow reads). Documents and labels
     * key on this; rules key on `isSettledExternally()`.
     */
    isChargedExternally() {
        return this.isSettledExternally() && !this.correctsOthers();
    }
    /**
     * An externally settled receipt that never registered fiscally (a legacy
     * `external` send) has exactly one document, the externally settled slip
     * — and so does its full-cancel corrective, which renders the slip as a
     * reversal. Registered settled receipts keep their fiscal receipt.
     */
    rendersExternallySettledSlip() {
        if (!this.isExternal())
            return false;
        if (this.correctsOthers())
            return this.correctsOrders().some(order => order.isSettledExternally());
        return this.isSettledExternally();
    }
    /**
     * Whether the receipt exists in the A4 format at all. An externally settled
     * charge is regular (receipt-printer) format only: the slip has no A4
     * layout, and a registered charge carries the externally settled block,
     * which only the regular format prints. Its storno corrective is an
     * ordinary receipt. Views disable the format switch when this is false.
     */
    hasA4Format() {
        if (this.rendersExternallySettledSlip())
            return false;
        return !this.isChargedExternally();
    }
    /**
     * Whether the order's document is a manual invoice: a plain commercial
     * document with no fiscal content, which the kit's manual-invoice body
     * renders. A manually registered invoice is one; a market may count more.
     */
    rendersManualInvoice() {
        return this.register_type === OrderRegisterType.manual;
    }
    /**
     * Whether the receipt exists in the regular (receipt-printer) format at
     * all. A credit note, an e-invoice and a manual invoice have only their A4
     * body — the kit's Receipt dispatch renders it whatever the format switch
     * says — so views lock the switch to A4 when this is false, and what the
     * screen shows is what a PDF is cut to.
     */
    hasRegularFormat() {
        return !(this.isEinvoice() || this.isCreditNote() || this.rendersManualInvoice());
    }
    /**
     * The flag encoding is positional (values comma-joined, flags
     * pipe-joined), so free-text values like PMS room names must not carry
     * either separator: a ',' would shift the positional reads, a '|' would
     * corrupt every other flag on the order. The backend sanitizes the same
     * way in its flag stamp.
     */
    static sanitizeFlagValue(value) {
        return value.replace(/[,|]/g, ' ').trim();
    }
    isZeroTaxMode() {
        const client = this.client || this.getClient();
        return this.export || client?.isDiplomat();
    }
    determineRegisterType() {
        const isFiscalStatus = Order.fiscalStatuses().includes(this.status);
        if (!EinvoiceService.einvoiceOnly()) {
            if (isFiscalStatus)
                return OrderRegisterType.fiscal;
            if (this.isCreditNote())
                return OrderRegisterType.manual;
        }
        return null;
    }
    /**
     * Check if register status of E-Invoice is failed
     */
    einvoiceIsFailed() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.failed;
    }
    /**
     * Check if register status of E-Invoice is sending
     */
    einvoiceIsSending() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceSending;
    }
    /**
     * Check if register status of E-Invoice is sent
     */
    einvoiceIsSent() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceSent;
    }
    /**
     * Check if register status of E-Invoice is rejected
     */
    einvoiceIsRejected() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceRejected;
    }
    /**
     * Check if register status of E-Invoice is approved
     */
    einvoiceIsApproved() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceApproved;
    }
    /**
     * Check if register status of E-Invoice is cancelled
     */
    einvoiceIsCancelled() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceCancelled;
    }
    /**
     * Check if register status of E-Invoice is storno
     */
    einvoiceIsStorno() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceStorno;
    }
    /**
     * Check if register status of E-Invoice is seen
     */
    einvoiceIsSeen() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceSeen;
    }
    /**
     * Check if register status of E-Invoice is new
     */
    einvoiceIsNew() {
        return this.isEinvoice() && this.register_status === OrderRegisterStatusType.einvoiceNew;
    }
    /**
     * Update Einvoice
     */
    async einvoiceUpdate(options) {
        const { action, comment } = options;
        const validation = new OrderValidation(this, true);
        const error = await validation.validateEinvoice(action);
        if (error) {
            return { status: 'failed', error };
        }
        return this.einvoiceSend(action, comment);
    }
    /**
     * Refresh Einvoice
     */
    async einvoiceRefresh() {
        if (!this.isEinvoice())
            return { status: 'failed' };
        if (!connectionService.isOnline)
            return { status: 'failed' };
        try {
            const response = await apiService.patch(`/orders/einvoice/${this.id}/refresh-sef-status`);
            if (!response.data.success)
                return { status: 'failed' };
            if (response.data.order) {
                this.$fill(response.data.order);
                Order.insert({ data: [response.data.order] });
            }
            return { status: 'success', order: this };
        }
        catch (error) {
            return { status: 'failed' };
        }
    }
    /**
     * Send E-Invoice
     */
    async einvoiceSend(updateAction, comment) {
        try {
            const url = `/orders/einvoice${updateAction ? `/${this.id}/${updateAction}` : '/process'}`;
            const method = updateAction ? 'patch' : 'post';
            if (updateAction) {
                this.register_request = this.register_request
                    ? { ...this.register_request, comment: comment }
                    : { comment };
            }
            const response = await apiService[method](url, { order: this.$toJson() });
            if (response.data.order) {
                if (response.data.order.hash === this.hash) {
                    this.$fill(response.data.order);
                }
                if (this.number) {
                    OrderService.setLatestNumber(this.number);
                }
                if (this.internal_number) {
                    OrderService.setLatestInternalNumber(this.internal_number);
                }
                Order.insert({ data: [response.data.order] });
            }
            if (response.data.ref_orders && response.data.ref_orders.length) {
                Order.insert({ data: response.data.ref_orders });
            }
            if (response.data.client) {
                Client.update(response.data.client);
            }
            if (response.data.product_counts && response.data.product_counts.length) {
                Product.update(response.data.product_counts);
            }
            if (response.data.error) {
                return { status: 'failed', error: I18n.t(`order.error.${response.data.error}`) };
            }
            return { status: 'success', order: this };
        }
        catch (error) {
            console.log(error.message);
            return { status: 'failed', error: I18n.t('order.error.order_process_failed') };
        }
    }
    /**
     * Set Export
     */
    setExport(isExport = false) {
        if (this.isSummary())
            return;
        const existingClient = this.client || this.getClient();
        const existingZeroTaxMode = this.export || existingClient?.isDiplomat();
        const zeroTaxMode = isExport || existingClient?.isDiplomat();
        this.export = isExport;
        if (zeroTaxMode) {
            this.setZeroItemsTax(isExport ? 'export' : 'diplomat');
        }
        else if (existingZeroTaxMode) {
            this.setOriginalItemsTax();
        }
    }
    /**
     * Set Zero Tax
     */
    setZeroItemsTax(reason) {
        if (this.isSummary())
            return;
        this.items = this.items.map(item => {
            item.setZeroTax(reason);
            return item;
        });
        this.taxes = Order.calculateTaxes(this);
    }
    /**
     * Set Original Items Tax
     */
    setOriginalItemsTax() {
        if (this.isSummary())
            return;
        this.items = this.items.map(item => {
            item.setOriginalItemsTax();
            return item;
        });
        this.taxes = Order.calculateTaxes(this);
    }
    /**
     * Set item separator
     */
    setItemSeparator() {
        const separatorIndex = '' + this.items.length;
        if (!this.flagsInclude(OrderFlagType.separator)) {
            this.setFlag(OrderFlagType.separator, separatorIndex);
            return;
        }
        let segments = this.getFlag(OrderFlagType.separator);
        if (segments.includes(separatorIndex)) {
            segments = segments.filter(segment => segment !== separatorIndex);
        }
        else {
            segments.push(separatorIndex);
        }
        if (!segments.length) {
            this.removeFlag(OrderFlagType.separator);
            return;
        }
        this.setFlag(OrderFlagType.separator, ...segments.sort((i1, i2) => +i1 - +i2));
    }
    /**
     * Get item separators
     */
    getItemSeparators() {
        return this.getFlag(OrderFlagType.separator);
    }
    /**
     * Add flag to the order
     */
    setFlag(key, ...args) {
        const flags = this.flag ? this.flag.split('|') : [];
        const flagIndex = flags.findIndex(flag => flag.startsWith(key));
        if (flagIndex !== -1) {
            flags[flagIndex] = `${key},${args.join(',')}`;
        }
        else {
            flags.push(`${key},${args.join(',')}`);
        }
        this.flag = flags.join('|');
    }
    /**
     * Get flag values
     */
    getFlag(key) {
        const flags = this.flag ? this.flag.split('|') : [];
        const flag = flags.find(flag => flag.startsWith(key + ','));
        if (!flag)
            return [];
        return flag.split(',').slice(1);
    }
    /**
     * Remove flag
     */
    removeFlag(key) {
        let flags = this.flag ? this.flag.split('|') : [];
        flags = flags.filter(flag => !flag.startsWith(key + ','));
        if (!flags.length) {
            this.flag = null;
            return;
        }
        this.flag = flags.join('|');
    }
    /**
     * Check if the flags include a specific flag
     */
    flagsInclude(value, prefix) {
        if (!this.flag)
            return false;
        if (!prefix)
            return this.flag.includes(value);
        const flags = this.flag.split('|');
        const flag = flags.find(flag => flag.startsWith(prefix + ','));
        if (!flag)
            return false;
        return flag.split(',').slice(1).some(val => val === value);
    }
    mergeFlags(flag1, flag2) {
        const flagList1 = flag1 ? flag1.split('|') : [];
        const flagList2 = flag2 ? flag2.split('|') : [];
        flagList2.forEach(newFlag => {
            const key = newFlag.split(',')[0];
            const flagIndex = flagList1.findIndex(flag => flag.startsWith(key));
            if (flagIndex !== -1) {
                flagList1[flagIndex] = newFlag;
            }
            else {
                flagList1.push(newFlag);
            }
        });
        return flagList1.join('|');
    }
    /**
     * Set the guest id on an order
     */
    setGuestId(guest) {
        this.setFlag(OrderFlagType.guest, guest + '');
    }
    /**
     * Get the guest id of the order
     */
    getGuestId() {
        const guestFlag = this.getFlag(OrderFlagType.guest);
        if (!guestFlag.length)
            return null;
        return parseInt(guestFlag[0]);
    }
    /**
     * Correct items of the regular orders with items
     * from the corrective orders in the array
     */
    static correctOrders(orders) {
        const correctiveOrders = orders.filter(order => order.correctsOthers());
        if (correctiveOrders.length === 0) {
            return orders;
        }
        const nonCorrectiveOrders = orders.filter(order => !order.correctsOthers());
        return nonCorrectiveOrders.map(order => {
            const correctedByOrder = correctiveOrders.find(o => {
                const correctsOrder = o.correctsOrders()[0];
                return correctsOrder && correctsOrder.hash === order.hash;
            });
            if (correctedByOrder) {
                order.items = order.items
                    .map(item => {
                    const correctedByItem = correctedByOrder.items.find(i => i.product_id === item.product_id && i.discount_percent === item.discount_percent);
                    item.count = correctedByItem
                        ? (item.getCount() + correctedByItem.getCount()).toFixed(3)
                        : item.count;
                    return item;
                })
                    .filter(item => item.getCount() > 0);
            }
            return order;
        });
    }
    async transferToTable(table, tableHasOrders) {
        if (tableHasOrders) {
            this.setFlag(OrderFlagType.tableTransfer, this.table_id + '', table.id + '');
        }
        this.table_id = table.id;
        const result = await this.sync('update');
        if (result.status === 'failed')
            return result;
        // this.hash, not this.order_hash — Order has no order_hash field, so
        // the old filter compared against undefined and never moved a single
        // kitchen ticket (locally, on the backend, or on the KDS).
        const kitchenTickets = KitchenTicket.all().filter(kt => kt.order_hash === this.hash);
        for (const kitchenTicket of kitchenTickets) {
            await kitchenTicket.transferToTable(table);
        }
        return result;
    }
    async transferToUser(user) {
        this.user_id = user.id;
        const result = await this.sync('update');
        if (result.status === 'failed')
            return result;
        // this.hash — see transferToTable above.
        const kitchenTickets = KitchenTicket.all().filter(kt => kt.order_hash === this.hash);
        for (const kitchenTicket of kitchenTickets) {
            await kitchenTicket.transferToUser(user);
        }
        return result;
    }
    getTaxes() {
        return [
            ...this.getTaxesOnItems(),
            ...this.getTaxesOnTotal()
        ];
    }
    getTaxesOnItems() {
        const taxItems = this.items.map(item => TaxItem.createForOrderItem(this, item))
            .filter((taxItem) => !!taxItem);
        return TaxItem.merge(taxItems);
    }
    getTaxesOnTotal() {
        const taxItems = TaxRate.all()
            .filter(taxRate => taxRate.type === TaxRate.TYPE_ORDER)
            .map(taxRate => TaxItem.createForTaxRateOnTotal(this, taxRate));
        return TaxItem.merge(taxItems);
    }
    /**
     * Calculate Taxes
     */
    static calculateTaxes(orders) {
        if (!Array.isArray(orders)) {
            orders = [orders];
        }
        if (orders.length === 1 && orders[0].isSummary()) {
            orders = orders[0].paysForOrders();
        }
        orders = orders.map(order => order.$duplicate());
        // if there are multiple orders correct regular ones with corrective ones
        if (orders.length > 1) {
            orders = Order.correctOrders(orders);
        }
        const taxItems = orders.filter(order => !order.isSummary())
            .flatMap(order => order.getTaxes());
        return TaxItem.merge(taxItems);
    }
    /** The fiscal verification QR payload (a market override); `venue` is the receipt's own venue. */
    async generateQRCodeString(_venue) {
        return '';
    }
    /**
     * Print receipt
     */
    async printReceipt(options) {
        const { isCopy, forceA4Format, choosePrinter, openDrawer, } = options;
        const printable = await this.getPrintable({
            isReceipt: true,
            isCopy,
            forceA4Format
        });
        return printable.print({
            choosePrinter,
            openDrawer
        });
    }
    /**
     * Print order
     */
    printSlips() {
        let items = {};
        Product.PRINTING_SECTORS.forEach(printingSector => {
            items[printingSector] = [];
        });
        this.items.forEach(item => {
            const product = item.getProduct();
            if (product?.printing_sector) {
                items[product.printing_sector].push(item);
            }
        });
        // print order
        const orderPrinter = PrintService.getAssignedPrinter(PrintRoleType.order);
        if (orderPrinter) {
            this.printSlip(orderPrinter, 'order');
        }
        // print in printing sectors
        let printerIndex = 1;
        Product.PRINTING_SECTORS.forEach(printingSector => {
            let assignedPrinter = PrintService.getAssignedPrinter(printingSector);
            if (items[printingSector].length && assignedPrinter) {
                const delay = printerIndex * 2000;
                setTimeout(() => {
                    this.printSlip(assignedPrinter, printingSector);
                }, delay);
                printerIndex++;
            }
        });
    }
    /**
     * Print order slip
     *
     * @param {Order} order
     * @param {Object} printer
     */
    async printSlip(printer, printingSector) {
        const printable = await this.getPrintable({
            isReceipt: false,
            isCopy: false,
            printingSector,
            forceA4Format: false
        });
        return printable.print({ printer });
    }
    /**
     * Get The Order Payment Type
     */
    paymentType() {
        // An unpaid receipt whose bill an external system holds is labeled by
        // that system ("Sent to room") rather than shown as an open order.
        if (this.isUnpaid()) {
            return (this.isChargedExternally() && settlementPresenter(this)?.paymentLabel()) || I18n.t('orders.payment_types.order');
        }
        if (this.isProforma())
            return I18n.t('orders.payment_types.proforma');
        let type = null;
        if (!this.getPaidCash().isZero()) {
            type = I18n.t('orders.payment_types.cash');
        }
        if (!this.getPaidCreditCard().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.credit_card');
        }
        if (!this.getPaidInvoice().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.invoice');
        }
        if (!this.getPaidCreditCardBusiness().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.credit_card_business');
        }
        if (!this.getPaidAdvance().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.advance');
        }
        if (!this.getPaidCheck().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.check');
        }
        if (!this.getPaidVoucher().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.voucher');
        }
        if (!this.getPaidMobileMoney().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.mobile_money');
        }
        if (!this.getPaidOtherNoncash().isZero()) {
            type = (type ? type + ', ' : '') + I18n.t('orders.payment_types.other_noncash');
        }
        return type;
    }
    isUnpaid() {
        return this.status === OrderStatusType.unpaid;
    }
    isPaid() {
        return this.status === OrderStatusType.paid;
    }
    isSummary() {
        return this.status === OrderStatusType.summary;
    }
    isCreditNote() {
        return this.status === OrderStatusType.creditNote;
    }
    isProforma() {
        return this.status === OrderStatusType.proforma;
    }
    isTraining() {
        return this.getFlag(OrderFlagType.mode).includes('training');
    }
    isQrOrder() {
        return this.source === 'qr';
    }
    /**
     * A platform order (API webhook or manual delivery entry). QR orders carry
     * a `source` too but are dine-in: they sit on a table and are prepared
     * like a waiter round, so no delivery presentation applies to them.
     */
    isDelivery() {
        return !!this.source && !this.isQrOrder();
    }
    isAdvance() {
        return this.status === OrderStatusType.advance;
    }
    refersToAdvance() {
        return this.refers_to.some(order => order.reference?.type === 'advance');
    }
    isCopy() {
        return this.status === OrderStatusType.copy;
    }
    isNonCash() {
        if (this.getToPay().toUnit() == 0)
            return true;
        return this.getPaidCash().toUnit() == 0 && this.getPaidCreditCard().toUnit() == 0;
    }
    correctsOthers() {
        return !this.canceled() && this.refers_to.some(order => order.reference?.type === 'corrective');
    }
    corrected() {
        return this.referred_by.some(order => order.reference?.type === 'corrective' && !order.canceled());
    }
    canceled() {
        return !!this.canceled_at;
    }
    unpaidNoSummary() {
        return this.isUnpaid() && !this.paidByOrder();
    }
    unpaidWithSummary() {
        return this.isUnpaid() && !!this.paidByOrder();
    }
    advanceClosed() {
        const ordersFromAdvanceTotal = this.ordersFromAdvance()
            .filter(order => !order.canceled())
            .reduce((acc, cur) => acc.add(cur.getPaidAdvance().abs()), Money.parse(0));
        return ordersFromAdvanceTotal.toUnit() >= this.getToPay().abs().toUnit();
    }
    ordersFromAdvance() {
        return this.referred_by.filter(order => order.reference?.type === 'advance');
    }
    referredAdvanceOrders() {
        return this.refers_to.filter(order => order.reference?.type === 'advance');
    }
    ordersFromProforma() {
        return this.referred_by.filter(order => order.reference?.type === 'proforma');
    }
    isSale() {
        return this.isPaid() || this.isAdvance() || (this.isUnpaid() && I18n.c('order.unpaidIssuesReceipt'));
    }
    isMoneyExchanged() {
        if (this.isTraining())
            return false;
        return this.isPaid() || this.isSummary() || (this.isAdvance() && !this.flagsInclude('closeAdvance') && !this.isAutomaticAdvanceClosing());
    }
    /**
     * The closing an advance gets for a final it names (`advanceFinal`), or the undo of one: it only corrects the
     * advance, so no money moves (advances used in parts).
     */
    isAutomaticAdvanceClosing() {
        const namesFinal = (order) => order.refers_to.some(ref => ref.reference?.type === 'advanceFinal');
        if (namesFinal(this))
            return true;
        // The undo's own copy of the closing carries no references; the stored closing does.
        const corrected = this.correctsOrders()[0];
        if (!corrected)
            return false;
        const stored = Order.find(corrected.hash);
        if (stored)
            return namesFinal(stored);
        // A closing this register no longer holds (an older one, or after a restart): where an advance is used in
        // parts, only a closing's undo corrects an advance's corrective, and its copy of the closing keeps the flag.
        return I18n.c('order.advanceInParts') && corrected.isAdvance()
            && (!!corrected.getFlag(OrderFlagType.correctType)[0] || corrected.flagsInclude('closeAdvance'));
    }
    isCashExchanged() {
        return this.isMoneyExchanged() && (this.getPaidCash().toUnit() !== 0 || this.getChange().toUnit() !== 0);
    }
    isMixedPayment() {
        let paymentTypesCount = 0;
        if (this.getPaidCash().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidCreditCard().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidInvoice().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidCreditCardBusiness().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidAdvance().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidCheck().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidVoucher().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidMobileMoney().toUnit() !== 0)
            paymentTypesCount++;
        if (this.getPaidOtherNoncash().toUnit() !== 0)
            paymentTypesCount++;
        return paymentTypesCount > 1;
    }
    getPaidCash() {
        return Money.parse(this.paid_cash);
    }
    getPaidCreditCard() {
        return Money.parse(this.paid_credit_card);
    }
    getPaidInvoice() {
        return Money.parse(this.paid_invoice);
    }
    getPaidCreditCardBusiness() {
        return Money.parse(this.paid_credit_card_business);
    }
    getPaidAdvance() {
        return Money.parse(this.paid_advance);
    }
    getPaidCheck() {
        return Money.parse(this.paid_check);
    }
    getPaidVoucher() {
        return Money.parse(this.paid_voucher);
    }
    getPaidMobileMoney() {
        return Money.parse(this.paid_mobile_money);
    }
    getPaidOtherNoncash() {
        return Money.parse(this.paid_other_noncash);
    }
    getChange() {
        return Money.parse(this.change);
    }
    getTip() {
        return Money.parse(this.tip);
    }
    getCashSale() {
        return this.getPaidCash().add(this.getPaidCreditCard());
    }
    getNonCashSale() {
        return this.getPaidInvoice()
            .add(this.getPaidCreditCardBusiness())
            .add(this.getPaidAdvance())
            .add(this.getPaidCheck())
            .add(this.getPaidVoucher())
            .add(this.getPaidMobileMoney())
            .add(this.getPaidOtherNoncash());
    }
    getLastActivityAt() {
        if (!this.last_activity_at) {
            return null;
        }
        return moment.utc(this.last_activity_at).local();
    }
    getTransactionDate() {
        return this.getLastActivityAt();
    }
    /**
     * Lazy Get User
     */
    getUser() {
        return User.find(this.user_id);
    }
    /**
     * Lazy Get Client
     */
    getClient() {
        return Client.find(this.client_id);
    }
    /**
     * Lazy Get Table
     */
    getTable() {
        return Table.find(this.table_id);
    }
    /**
     * Lazy Get Sector
     */
    getSector() {
        const table = this.getTable();
        if (table)
            return Sector.find(table.sector_id);
        return undefined;
    }
    // Additional getters
    getPrice() {
        if (this.isSummary()) {
            const amount = this.paysForOrders().reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0));
            const isRefund = !!this.getFlag(OrderFlagType.correctType)[0];
            return amount.multiply(isRefund ? -1 : 1);
        }
        if (this.isCreditNote()) {
            return this.getToPay();
        }
        return this.items.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0));
    }
    getToPay() {
        if (this.isSummary()) {
            const amount = this.paysForOrders().reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
            const isRefund = !!this.getFlag(OrderFlagType.correctType)[0];
            return amount.multiply(isRefund ? -1 : 1);
        }
        if (this.isCreditNote()) {
            return this.items.reduce((acc, cur) => acc.add(cur.getAdditionalToPay()), Money.parse(0));
        }
        return this.items.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
    }
    getLeftToPay() {
        return this.getToPay().subtract(this.getPaidAdvanceEffective());
    }
    getPriceBeforeTax() {
        if (this.isSummary()) {
            const amount = this.paysForOrders().reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0));
            const isRefund = !!this.getFlag(OrderFlagType.correctType)[0];
            return amount.multiply(isRefund ? -1 : 1);
        }
        if (this.isCreditNote()) {
            return this.items.reduce((acc, cur) => acc.add(cur.getAdditionalPriceBeforeTax()), Money.parse(0));
        }
        return this.items.reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0));
    }
    getExemptFromTax() {
        if (this.isSummary()) {
            const amount = this.paysForOrders().reduce((acc, cur) => acc.add(cur.getExemptFromTax()), Money.parse(0));
            const isRefund = !!this.getFlag(OrderFlagType.correctType)[0];
            return amount.multiply(isRefund ? -1 : 1);
        }
        if (this.isCreditNote()) {
            return this.items
                .filter(item => !!item.exempt_from_tax)
                .reduce((acc, cur) => acc.add(cur.getAdditionalToPay()), Money.parse(0));
        }
        return this.items
            .filter(item => !!item.exempt_from_tax)
            .reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
    }
    getTax() {
        if (this.isSummary()) {
            const amount = this.paysForOrders().reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
            const isRefund = !!this.getFlag(OrderFlagType.correctType)[0];
            return amount.multiply(isRefund ? -1 : 1);
        }
        if (this.isCreditNote()) {
            return this.items
                .filter(item => !item.exempt_from_tax)
                .reduce((acc, cur) => acc.add(cur.getAdditionalTax()), Money.parse(0));
        }
        return this.items
            .filter(item => !item.exempt_from_tax)
            .reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
    }
    getDiscountAmount() {
        if (this.isSummary()) {
            const amount = this.paysForOrders().reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0));
            const isRefund = !!this.getFlag(OrderFlagType.correctType)[0];
            return amount.multiply(isRefund ? -1 : 1);
        }
        if (this.isCreditNote()) {
            return Money.parse(0);
        }
        return this.items.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0));
    }
    getAdditionalDiscountAmount() {
        return this.items
            .filter(item => item.getAdditionalDiscountPercent() != 0)
            .reduce((acc, cur) => acc.add(cur.getAdditionalPriceBeforeTax()), Money.parse(0));
    }
    getAdditionalCountAmount() {
        return this.items
            .filter(item => item.getAdditionalCount() != 0)
            .reduce((acc, cur) => acc.add(cur.getAdditionalPriceBeforeTax()), Money.parse(0));
    }
    getPaid() {
        return this.getPaidCash()
            .add(this.getPaidCreditCard())
            .add(this.getPaidInvoice())
            .add(this.getPaidCreditCardBusiness())
            .add(this.getPaidAdvance())
            .add(this.getPaidCheck())
            .add(this.getPaidVoucher())
            .add(this.getPaidMobileMoney())
            .add(this.getPaidOtherNoncash())
            .add(this.getChange());
    }
    getPayments() {
        return [
            { key: PaymentTypeLabel.cash, amount: Money.parse(this.paid_cash).toUnit(), display: I18n.t('orders.payment_types.cash') },
            { key: PaymentTypeLabel.credit_card, amount: Money.parse(this.paid_credit_card).toUnit(), display: I18n.t('orders.payment_types.credit_card') },
            { key: PaymentTypeLabel.invoice, amount: Money.parse(this.paid_invoice).toUnit(), display: I18n.t('orders.payment_types.invoice') },
            { key: PaymentTypeLabel.credit_card_business, amount: Money.parse(this.paid_credit_card_business).toUnit(), display: I18n.t('orders.payment_types.credit_card_business') },
            { key: PaymentTypeLabel.advance, amount: Money.parse(this.paid_advance).toUnit(), display: I18n.t('orders.payment_types.advance') },
            { key: PaymentTypeLabel.check, amount: Money.parse(this.paid_check).toUnit(), display: I18n.t('orders.payment_types.check') },
            { key: PaymentTypeLabel.voucher, amount: Money.parse(this.paid_voucher).toUnit(), display: I18n.t('orders.payment_types.voucher') },
            { key: PaymentTypeLabel.mobile_money, amount: Money.parse(this.paid_mobile_money).toUnit(), display: I18n.t('orders.payment_types.mobile_money') },
            { key: PaymentTypeLabel.other_noncash, amount: Money.parse(this.paid_other_noncash).toUnit(), display: I18n.t('orders.payment_types.other_noncash') },
        ].filter(p => p.amount != 0);
    }
    getPaidAdvanceEffective() {
        const paidAdvance = Money.parse(this.getFlag(OrderFlagType.advance)[0] || 0);
        if (paidAdvance.toUnit() == 0)
            return Money.parse(0);
        if (paidAdvance.toUnit() > 0 && paidAdvance.toUnit() > this.getToPay().toUnit())
            return this.getToPay();
        if (paidAdvance.toUnit() < 0 && paidAdvance.toUnit() < this.getToPay().toUnit())
            return this.getToPay();
        return paidAdvance;
    }
    getCorrectedAmount() {
        return this.correctedByOrders().reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
    }
    getRemainingAdvanceAmount() {
        return this.getToPay().add(this.getCorrectedAmount());
    }
    getRemainingTaxes() {
        const correctedByOrdersTaxes = this.correctedByOrders().flatMap(o => o.taxes);
        return TaxItem.merge([...correctedByOrdersTaxes, ...this.taxes]);
    }
    async getIic(iicSignature) {
        return '';
    }
    /** `venue` is the receipt's own venue when the caller has it, else the logged-in tenant's. */
    getFullNumber(venue) {
        const resolvedVenue = venue ?? Store.get('tenant')?.venue;
        if (!resolvedVenue)
            return '';
        // aria-api composes the same attribute with the year pinned to
        // Europe/Podgorica (PHP format('Y')); the printed number must match it
        // on a device in any zone. 'YYYY', not 'yy' — lowercase y tokens are
        // moment's ERA year, whose value shifts with the zone that primed the
        // era cache.
        const issueDateTime = moment.utc(this.last_activity_at).tz('Europe/Podgorica');
        let cashRegister = CashRegister.find(this.cash_register_id);
        return (resolvedVenue.hash +
            '/' +
            this.number +
            '/' +
            issueDateTime.format('YYYY') +
            (cashRegister ? '/' + cashRegister.hash : ''));
    }
    allowCancel() {
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        if (this.isIncoming())
            return false;
        // External documents are immutable. An externally settled one may be
        // cancelled in full only where the market allows it: the corrective is
        // a local reversal that never reaches the system holding the bill.
        if (this.isExternal() && !(this.isSettledExternally() && I18n.c('order.allowExternallySettledCancel')))
            return false;
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();
        if (!curCashRegister || !curShift)
            return false;
        if (!this.cash_register_id ||
            this.correctsOthers() ||
            this.corrected() ||
            this.canceled() ||
            this.unpaidWithSummary() ||
            this.cancelBlockedByAdvance()) {
            return false;
        }
        if (this.einvoiceIsFailed() ||
            this.einvoiceIsRejected() ||
            this.einvoiceIsCancelled() ||
            this.einvoiceIsStorno()) {
            return false;
        }
        return (this.isPaid() ||
            this.isUnpaid() ||
            this.isAdvance() ||
            this.isSummary() ||
            this.isTraining() ||
            (this.isProforma() && this.isFiscal()));
    }
    /**
     * Reverse refund is at the moment limited to the advance refunds that close
     * one or more  advance receipts and are used as the base for the final receipt
     *
     * @returns boolean
     */
    allowRefundReverse() {
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        if (this.isIncoming())
            return false;
        if (this.isExternal())
            return false;
        // An externally settled order may only be cancelled in full; reversing
        // a refund of one (or of its storno corrective) is not allowed.
        if (this.isSettledExternally() || this.correctsOrders().some(order => order.isSettledExternally()))
            return false;
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();
        if (!curCashRegister || !curShift)
            return false;
        if (!this.cash_register_id || this.canceled() || this.corrected())
            return false;
        if (this.isAdvance()) {
            const hasFinalReceipt = this.ordersFromAdvance().some(order => !order.canceled());
            if (hasFinalReceipt)
                return false;
        }
        const type = this.getFlag(OrderFlagType.type);
        const hasFlag = type[0] === 'closeAdvance' && type[1] !== 'correct';
        return I18n.c('order.allowAdvanceClose')
            && hasFlag
            && this.getToPay().toUnit() < 0;
    }
    allowCorrect() {
        if (this.isEinvoice())
            return false;
        if (this.isIncoming())
            return false;
        if (this.isExternal())
            return false;
        // Externally settled orders may only be cancelled in full — a partial
        // correction would leave the external ledger and the receipt out of step.
        if (this.isSettledExternally())
            return false;
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        if (this.isMixedPayment())
            return false;
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();
        if (!curCashRegister || !curShift)
            return false;
        if (!this.cash_register_id ||
            this.correctsOthers() ||
            this.corrected() ||
            this.canceled() ||
            this.unpaidWithSummary() ||
            this.refersToAdvance()) {
            return false;
        }
        return this.isPaid();
    }
    allowCopy() {
        if (!I18n.c('order.allowReceiptCopy') || !this.isFiscal())
            return false;
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        if (this.isIncoming())
            return false;
        if (this.isExternal())
            return false;
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();
        if (!curCashRegister || !curShift)
            return false;
        if (!this.cash_register_id || this.corrected() || this.canceled() || this.unpaidWithSummary()) {
            return false;
        }
        return this.isPaid() || this.isAdvance() || this.isTraining() || this.isProforma();
    }
    allowAdvanceLinkNew() {
        if (!I18n.c('order.allowAdvanceLinked') || !this.isFiscal())
            return false;
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        if (this.isIncoming())
            return false;
        if (this.isExternal())
            return false;
        if (!curCashRegister || !curShift)
            return false;
        if (!this.cash_register_id || this.advanceClosed())
            return false;
        if (this.corrected() || this.canceled())
            return false;
        return this.isAdvance();
    }
    allowAdvanceFinal() {
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();
        if (!!Store.get('trainingMode') !== !!this.isTraining())
            return false;
        if (this.isIncoming())
            return false;
        if (this.isExternal())
            return false;
        if (!curCashRegister || !curShift)
            return false;
        if (!this.cash_register_id || this.advanceClosed())
            return false;
        if (I18n.c('order.allowAdvanceClose') && !this.einvoiceIsApproved() && (!this.flagsInclude('closeAdvance') || this.getToPay().toUnit() >= 0))
            return false;
        // Where the final is issued from the advance receipt, a corrective (a closing, or the undo of
        // one after its final was cancelled) is never the source: the advance itself is.
        if (!I18n.c('order.allowAdvanceClose') && (this.getToPay().toUnit() < 0 || this.correctsOthers()))
            return false;
        if ((this.corrected() || this.canceled()) && !this.isAdvancePartlyClosed())
            return false;
        return this.isAdvance();
    }
    /**
     * An advance receipt final receipts have used only in part: every correction standing on it is
     * a partial closing (type closeAdvance, correct — prepareAdvanceCorrect) and some of it is left.
     * It stays open for the rest; advanceClosed() ends it once the finals have used the whole amount
     * (also when an older final used part of it without a closing).
     */
    isAdvancePartlyClosed() {
        const closings = this.correctedByOrders();
        return this.isAdvance() && closings.length > 0 && !this.advanceClosed()
            && closings.every(order => {
                const type = order.getFlag(OrderFlagType.type);
                return type.includes('closeAdvance') && type.includes('correct');
            })
            && this.getRemainingAdvanceAmount().toUnit() > 0;
    }
    allowEinvoiceFromFiscal() {
        if (!connectionService.isOnline)
            return false;
        if (!EinvoiceService.isEnabled() || !this.isFiscal())
            return false;
        if (!!Store.get('trainingMode') || !!this.isTraining())
            return false;
        if (this.isIncoming())
            return false;
        if (this.isExternal())
            return false;
        if (!this.allowCancel())
            return false;
        const client = this.getClient();
        if (client?.id_type !== 'tax_id_number_local_jbkjs')
            return false;
        return this.isPaid();
    }
    /**
     * Get the orders that correct this order
     */
    correctedByOrders() {
        return this.referred_by.filter(order => order.reference?.type === 'corrective' && !order.canceled());
    }
    /**
     * Get the order corrected by this order
     */
    correctsOrders() {
        if (this.canceled())
            return [];
        return this.refers_to.filter(order => order.reference?.type === 'corrective');
    }
    /**
     * Get the order that this order is a copy of
     */
    copiesOrder() {
        return this.refers_to.find(order => order.reference && order.reference.type === 'copy');
    }
    /**
     * Get the order that pays for this order
     */
    paidByOrder() {
        return this.referred_by.find(order => {
            // Every correction stamps the bill canceled (it locks the receipt); a partly corrected
            // bill still pays its rounds, any other stamp releases them.
            const stored = Order.find(order.hash);
            const isStamped = order.canceled() || stored?.canceled() || false;
            const isPartlyCorrected = order.isPartlyCorrected() || !!stored?.isPartlyCorrected();
            const isSummaryRefund = order.isSummary() && !!order.getFlag(OrderFlagType.correctType)[0];
            return order.reference?.type === 'summary' && !(isStamped && !isPartlyCorrected) && !isSummaryRefund;
        });
    }
    /** Stamped by a partial correction (flag `corrected,partial`): locked, not cancelled. */
    isPartlyCorrected() {
        return this.getFlag(OrderFlagType.corrected)[0] === 'partial';
    }
    /** This corrective corrects only partly (correctType correct / correctSummary, not a cancel). */
    correctsPartly() {
        return ['correct', 'correctSummary'].includes(this.getFlag(OrderFlagType.correctType)[0]);
    }
    /**
     * Get all orders that this order pays for
     */
    paysForOrders() {
        return this.refers_to.filter(order => order.reference && order.reference.type === 'summary');
    }
    /**
     * Format order as html
     *
     * @param {boolean} options.isReceipt is order a receipt
     * @param {boolean} options.isCopy is order a copy
     * @param {string} options.printingSector is order unpaid for a certain printing sector
     * @param {boolean} options.forceA4Format force A4 form
     * @param {IVenueBase} venue Venue
     */
    async getPrintable(options, venue) {
        let printable;
        if (this.rendersExternallySettledSlip()) {
            // Never registered fiscally: the slip is the one and only document,
            // for every view, reprint, PDF and the journal alike.
            printable = this.getExternallySettledSlipPrintable([this], venue);
        }
        else {
            printable = options.isReceipt
                ? await this.getOrderPrintable(options, venue)
                : await this.getUnpaidOrderPrintable(options, venue);
            // A registered charge keeps its layout untouched and gets the
            // externally settled block appended.
            if (options.isReceipt && this.isChargedExternally())
                this.appendExternallySettledBlock(printable);
        }
        const RenderComponent = diContainer.resolve('orderRenderComponent');
        printable.setRenderSettings({
            component: RenderComponent,
            props: {
                order: this,
                options,
                venue: venue || Store.get('tenant')?.venue,
                isRetail: Store.get('isRetail')
            }
        });
        return printable;
    }
    async toText(charsPerLine = 32) {
        const isReceipt = !(this.isUnpaid() && !I18n.c('order.unpaidIssuesReceipt'));
        const printable = await this.getPrintable({ isReceipt });
        const printableResult = await printable.toText({ charsPerLine });
        return printableResult.forPrint();
    }
    async getUnpaidOrderPrintable(options, venue) {
        const pr = new Printable();
        const { printingSector } = options;
        const curShift = Shift.current();
        const curShiftOrders = curShift ? curShift.getOrders() : [];
        const orderNumber = curShiftOrders.length + 1;
        // Prefer the stable callout code stored at fire time; fall back to the
        // live shift counter for historical orders issued before callout codes.
        const displayCode = this.getFlag(OrderFlagType.calloutCode)?.[0] || String(orderNumber);
        pr.l();
        const isRefunded = this.canceled() || this.getToPay().toUnit() < 0;
        if (isRefunded) {
            pr.l('L', I18n.t('general.order_storno').toUpperCase(), { align: 'C', scale: '3,3' });
            pr.l();
        }
        if (printingSector) {
            pr.l('L', `${displayCode} # ${I18n.t('products.printing_sector.' + printingSector)}`, { align: 'C', scale: '2,2' });
            pr.l();
        }
        pr.l('D', '', { delimiter: '-' });
        if (this.isDelivery()) {
            // The banner above carries the callout; this line only names the
            // platform so the kitchen knows the order leaves the venue.
            const platformLabel = this.source.charAt(0).toUpperCase() + this.source.slice(1);
            pr.l('L', platformLabel, { scale: '2,2' });
        }
        else {
            const table = this.getTable();
            if (table) {
                const sector = this.getSector();
                const tableLabel = table.name + (sector ? ` (${sector.name})` : '');
                const guestId = this.getGuestId();
                const guestLabel = guestId !== null ? ` / G${guestId + 1}` : '';
                // A guest-placed round: the kitchen sees the table like any
                // waiter round, marked so staff know nobody keyed it in.
                const sourceLabel = this.isQrOrder() ? ' / QR' : '';
                pr.l('L', `${I18n.t('general.table')}: ${tableLabel}${guestLabel}${sourceLabel}`);
            }
        }
        const lastActivityAt = this.getLastActivityAt();
        pr.l('L', `${I18n.t('general.time')}: ${lastActivityAt ? formatDateTimeHuman(lastActivityAt) : ''}`);
        pr.l('B', `${I18n.t('general.issued_by')}:`, this.getUser()?.name || '');
        pr.l('D', '', { delimiter: '-' });
        // Product Items
        let productItems = this.items.map((item, index) => {
            item.index = index;
            return item;
        });
        // Item separators
        const separators = this.getItemSeparators();
        if (printingSector && printingSector !== 'order') {
            productItems = productItems.filter((item) => {
                const product = item.getProduct();
                return !product || product.printing_sector === printingSector;
            });
        }
        pr.l();
        productItems.forEach((item, index) => {
            if (index !== 0 && separators.length && separators[0] <= item.index) {
                pr.l('D', '', { delimiter: '-' });
                pr.l();
                separators.shift();
            }
            const count = isRefunded ? -1 * Math.abs(item.getCount()) : item.getCount();
            pr.l('L', `${count} x ${item.name}`, { scale: '2,2' });
            if (item.comments.length) {
                pr.l();
            }
            item.comments.forEach((comment) => {
                pr.l('L', `- ${comment}`);
            });
            pr.l();
        });
        if (this.isDelivery() && this.note) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('L', this.note);
        }
        return pr;
    }
    async getPackingSlipPrintable() {
        const pr = new Printable();
        const platformLabel = (this.source || '').toUpperCase();
        // The packing slip is read by the COURIER on the bag: lead with the
        // platform's own order reference (what their app shows). Manual
        // entries have no external code — fall back to the callout. When
        // both exist, the callout follows as a small internal staff line.
        const externalCode = this.getFlag(OrderFlagType.externalCode)?.[0] || '';
        const calloutCode = this.getFlag(OrderFlagType.calloutCode)?.[0] || '';
        const headerCode = externalCode || calloutCode;
        // Leading blank line for the printer's top margin, matching the
        // kitchen slips (Order.getSlipPrintable / OrderTicket.getSlipPrintable).
        pr.l();
        // '2,2' is the largest scale the print pipeline supports
        // (PrintableText maps only '2' to large width/height).
        pr.l('L', `${platformLabel} #${headerCode}`, { align: 'C', scale: '2,2' });
        if (externalCode && calloutCode) {
            pr.l('L', `#${calloutCode}`, { align: 'C' });
        }
        pr.l();
        pr.l('D', '', { delimiter: '=' });
        this.items.forEach((item) => {
            const count = item.getCount();
            pr.l('L', `${count} x ${item.name}`, { scale: '2,2' });
            item.comments.forEach((comment) => {
                pr.l('L', `    ${comment}`);
            });
            pr.l();
        });
        pr.l('D', '', { delimiter: '=' });
        if (this.note) {
            pr.l('L', this.note, { scale: '2,2' });
            pr.l();
        }
        // Payment line only when the platform actually told us (API tickets
        // set the deliveryPayment flag). Manual entries have no
        // platform-assigned payment method — printing "PREPAID" for them
        // would be a guess and misleads the courier on COD orders.
        const deliveryPayment = this.getFlag(OrderFlagType.deliveryPayment)[0];
        if (deliveryPayment) {
            const paymentLabel = deliveryPayment === 'CASH'
                ? I18n.t('order_ticket.cash_on_delivery')
                : I18n.t('order_ticket.prepaid');
            pr.l('L', paymentLabel, { align: 'C' });
        }
        return pr;
    }
    /**
     * The externally settled slip: the non-fiscal paper printed when a bill is
     * settled outside the POS (charged to a hotel room, for instance) and, for
     * receipts that never register fiscally, the one document every view and
     * reprint renders. One version for every market — the banner lines go
     * through `LatToCyr.t`, which converts to Cyrillic only when the venue's
     * own `cyrilic_receipt` setting says so. What the settlement looked like
     * (the detail lines, the closing note) comes from the settling system's
     * presenter.
     *
     * @param round every receipt settled together with this one (one slip per round)
     */
    getExternallySettledSlipPrintable(round = [this], venue) {
        const pr = new Printable();
        const resolvedVenue = venue || Store.get('tenant')?.venue;
        if (!resolvedVenue)
            return pr;
        const orders = round.length ? round : [this];
        const receiptHeaderText = resolvedVenue.receipt_note?.split('|')[1] || '';
        // A full-cancel corrective carries no settlement of its own: the slip
        // describes the charge it reverses.
        const charge = this.isChargedExternally()
            ? this
            : (this.correctsOrders().find(corrected => corrected.isChargedExternally()) ?? this);
        const presenter = settlementPresenter(charge);
        const receiptLogo = receiptLogoFor(resolvedVenue);
        if (receiptLogo)
            pr.l('LOGO', receiptLogo);
        // Venue header, closed by the non-fiscal banner
        pr.l('D', LatToCyr.t(I18n.t('receipt.nonfiscal_receipt')), { delimiter: '==', align: 'C', scale: '1,2' });
        pr.l();
        pr.l('L', resolvedVenue.company_name, { align: 'C' });
        pr.l('L', resolvedVenue.name, { align: 'C' });
        if (resolvedVenue.tax_id_number && resolvedVenue.tax_id_number.length !== 13) {
            pr.l('L', `${I18n.t('general.tax_id_number')}: ${resolvedVenue.tax_id_number}`, { align: 'C' });
        }
        pr.l('L', resolvedVenue.address, { align: 'C' });
        if (receiptHeaderText) {
            receiptHeaderText.split('\n').forEach(line => pr.l('L', line, { align: 'C' }));
        }
        pr.l();
        pr.l('D', '', { delimiter: '--' });
        pr.l();
        // Client
        const client = this.getClient();
        if (client?.name) {
            pr.l('L', `${I18n.t('general.buyer')}: ${client.name}`);
            if (client.address) {
                pr.l('L', `${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`);
            }
            let taxIdNumberPrinted = false;
            if ((client.country && client.country !== 'MNE') && client.id_number) {
                const idType = I18n.t(`general.${client.id_type}`);
                pr.l('L', `${idType}: ${client.id_number}`);
                taxIdNumberPrinted = client.id_type === ClientIDType.tax_id_number;
            }
            if (!taxIdNumberPrinted && client.tax_id_number) {
                pr.l('L', `${I18n.t('general.tax_id_number')}: ${client.tax_id_number}`);
            }
            if (client.tax_id_card_number) {
                pr.l('L', `${I18n.t('general.tax_id_card_number')}: ${client.tax_id_card_number}`);
            }
            pr.l('D', '', { delimiter: '-' });
        }
        // Subheader — the settlement moment when the receipt carries one, else
        // the receipt's own time
        const time = this.external_synced_at
            ? moment.utc(this.external_synced_at).local()
            : (this.getLastActivityAt() || moment());
        pr.l('L', `${I18n.t('general.time')}: ${time.format('DD.MM.YYYY. HH:mm:ss')}`);
        pr.l('L', `${I18n.t('general.operator')}: ${this.getUser()?.name || ''}`);
        presenter?.detailLines(charge).forEach(line => pr.l('L', `${line.label}: ${line.value}`));
        if (this.getTable()) {
            pr.l('L', `${I18n.t('general.table')}: ${this.getTable()?.name}`);
        }
        if (this.correctsOthers()) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('L', LatToCyr.t(I18n.t('receipt.externally_settled_reversal')), { align: 'C', style: { fontWeight: '600' } });
        }
        // Items — a receipt that closed several rounds already carries them merged
        const items = orders.flatMap(o => o.items);
        if (items.length) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('general.item')} (${I18n.t('tax_rates.rate')})`, I18n.t('general.price'));
            pr.l('D', '', { delimiter: '-' });
        }
        items.forEach(item => {
            const unitPrice = item.getUnitPriceAfterTax().toFormat('0.00');
            const price = item.getPrice().toFormat('0.00');
            const discountPercent = item.getDiscountPercent();
            const discountAmount = item.getDiscountAmount().multiply(-1).toFormat('0.00');
            const toPay = item.getToPay().toFormat('0.00');
            pr.l('L', `${item.name} (${item.getTaxRate()}%)`);
            pr.l('S', `   ${item.getCount()} x ${unitPrice}`, price);
            if (discountPercent > 0) {
                pr.l('S', `   ${I18n.t('general.discount')} (${discountPercent}%)`, discountAmount);
                pr.l('S', `   ${I18n.t('general.with_discount')}`, toPay);
            }
        });
        // Totals
        const total = orders.reduce((sum, o) => sum.add(o.getPrice()), Money.parse(0));
        const discount = orders.reduce((sum, o) => sum.add(o.getDiscountAmount()), Money.parse(0));
        const toPay = orders.reduce((sum, o) => sum.add(o.getToPay()), Money.parse(0));
        pr.l('D', '', { delimiter: '-' });
        pr.l('S', I18n.t('general.total'), total.toFormat('0.00'));
        pr.l('S', I18n.t('general.discount'), discount.toFormat('0.00'));
        pr.l('D', '', { delimiter: '-' });
        pr.l('S', I18n.t('general.to_pay').toUpperCase(), toPay.toFormat('0.00'), { scale: '2,2' });
        pr.l('D', '', { delimiter: '==' });
        pr.l('L', LatToCyr.t(I18n.t('receipt.nonfiscal_receipt')), { align: 'C', scale: '1,2', style: { fontWeight: '600' } });
        pr.l('D', '', { delimiter: '==' });
        // Order note(s)
        let note = orders.map(o => o.note).filter(Boolean).join('\n');
        const vatNote = vatExemptNote(resolvedVenue, '* ' + I18n.t('order.exempt_from_tax_comment'));
        if (vatNote) {
            note = vatNote + '\n\n' + note;
        }
        if (note) {
            note.split('\n').forEach(line => pr.l('L', line));
            pr.l('D', '', { delimiter: '-' });
        }
        // Closing: what this document is, and where the fiscal receipt is. A
        // reversal says what it is through the marker above; the charge's note
        // ("your bill was transferred to the room") would be false on it.
        const closingNote = charge === this ? presenter?.slipNote(this) : undefined;
        if (closingNote) {
            pr.l();
            pr.l('L', closingNote);
            pr.l();
        }
        pr.l();
        pr.l('D', LatToCyr.t(I18n.t('receipt.nonfiscal_receipt_end')), { delimiter: '==', align: 'C', scale: '1,2' });
        pr.l();
        return pr;
    }
    /**
     * The externally settled block under a registered charge's regular
     * receipt: the presenter's detail lines and its receipt note between two
     * dividers. The layouts themselves never know it exists.
     */
    appendExternallySettledBlock(printable) {
        const presenter = settlementPresenter(this);
        const lines = presenter?.detailLines(this) ?? [];
        const note = presenter?.receiptNote(this) ?? '';
        if (!lines.length && !note)
            return;
        printable.l('D', '', { delimiter: '-' });
        lines.forEach(line => printable.l('L', `${line.label}: ${line.value}`));
        if (note)
            printable.l('L', note);
        printable.l('D', '', { delimiter: '-' });
    }
    async getOrderPrintable(options, venue) {
        const priceFormat = '0,0.00';
        const pr = new Printable();
        const { isCopy } = options;
        let client = this.client || Client.find(this.client_id);
        const tenant = Store.get('tenant');
        const resolvedVenue = venue || tenant?.venue;
        if (!resolvedVenue)
            return pr;
        const receiptNote = resolvedVenue?.receipt_note?.split('|')[0] || '';
        const receiptHeaderText = resolvedVenue?.receipt_note?.split('|')[1] || '';
        const receiptLogo = receiptLogoFor(resolvedVenue);
        if (receiptLogo)
            pr.l('LOGO', receiptLogo);
        // Receipt Header
        pr.l('D', '', { delimiter: '=' });
        pr.l('L', resolvedVenue.company_name, { align: 'C' });
        pr.l('L', resolvedVenue.name, { align: 'C' });
        if (resolvedVenue.tax_id_number && resolvedVenue.tax_id_number.length !== 13) {
            pr.l('L', `${I18n.t('general.tax_id_number')}: ${resolvedVenue.tax_id_number}`, { align: 'C' });
        }
        pr.l('L', resolvedVenue.address, { align: 'C' });
        if (receiptHeaderText) {
            receiptHeaderText.split('\n').forEach(line => pr.l('L', line, { align: 'C' }));
        }
        if (this.isProforma()) {
            pr.l('L', I18n.t('general.proforma'), { align: 'C' });
        }
        else if (isCopy && !resolvedVenue?.settings?.hide_receipt_copy_label) {
            pr.l('L', I18n.t('general.receipt_copy'), { align: 'C' });
        }
        pr.l('D', '', { delimiter: '=' });
        // Client
        if (client && client.name) {
            pr.l('L', `${I18n.t('general.buyer')}: ${client.name}`);
            if (client.address) {
                pr.l('L', `${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`);
            }
            let taxIdNumberPrinted = false;
            if ((client.country && client.country !== 'MNE') && client.id_number) {
                const idType = I18n.t(`general.${client.id_type}`);
                pr.l('L', `${idType}: ${client.id_number}`);
                taxIdNumberPrinted = client.id_type === ClientIDType.tax_id_number;
            }
            if (!taxIdNumberPrinted && client.tax_id_number) {
                pr.l('L', `${I18n.t('general.tax_id_number')}: ${client.tax_id_number}`);
            }
            if (client.tax_id_card_number) {
                pr.l('L', `${I18n.t('general.tax_id_card_number')}: ${client.tax_id_card_number}`);
            }
            pr.l('D', '', { delimiter: '-' });
        }
        // Receipt Subheader
        // Delivery callout (e.g., "Glovo #A5") — shown for delivery orders
        // so the cashier, kitchen, and rider all reference the same identifier.
        const calloutCode = this.getFlag(OrderFlagType.calloutCode)?.[0];
        if (this.source && calloutCode) {
            const platformLabel = this.source.charAt(0).toUpperCase() + this.source.slice(1);
            pr.l('B', `${platformLabel} #${calloutCode}`);
        }
        if (this.internal_number) {
            pr.l('B', !this.isProforma() ? I18n.t('invoice.number') : I18n.t('invoice.proforma.number'), this.internal_number);
        }
        if (this.number) {
            pr.l('L', `${I18n.t('general.receipt_number')}: ${this.number}`);
        }
        const lastActivityAt = this.getLastActivityAt();
        pr.l('L', `${I18n.t('general.time')}: ${lastActivityAt ? formatDateTime(lastActivityAt) : ''}`);
        if (!this.isIncoming()) {
            pr.l('L', `${I18n.t('general.operator')}: ${this.getUser()?.name || ''}`);
            pr.l('L', `${I18n.t('general.operator_code')}: ${this.getUser()?.hash || ''}`);
        }
        if (this.paymentType() && !this.isProforma()) {
            pr.l('L', `${I18n.t('general.paid_by')}: ${this.paymentType()}`);
        }
        if (this.isAdvance()) {
            pr.l('L', `${I18n.t('general.receipt_type')}: ${I18n.t('general.advance')}`);
        }
        if (this.getTable()) {
            pr.l('L', `${I18n.t('general.table')}: ${this.getTable()?.name || ''}`);
        }
        if (this.items.length) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('general.item')} (${I18n.t('tax_rates.rate')})`, I18n.t('general.price'));
            pr.l('D', '', { delimiter: '-' });
        }
        const itemCommentsOnFiscalReceipts = !!resolvedVenue?.settings?.item_comments_on_fiscal_receipts;
        this.items.forEach((item) => {
            const count = item.getCount();
            const name = item.name;
            const unitPrice = item.getUnitPriceAfterTax().toFormat(priceFormat);
            const price = item.getPrice().toFormat(priceFormat);
            const discountPercent = item.getDiscountPercent();
            const discountAmount = item.getDiscountAmount().multiply(-1).toFormat(priceFormat);
            const toPay = item.getToPay().toFormat(priceFormat);
            pr.l('L', `${name} (${item.getTaxRate()}%)`);
            if (itemCommentsOnFiscalReceipts && item.comments.length) {
                item.comments.join('\n').split('\n').forEach((line) => pr.l('L', line));
            }
            pr.l('S', `   ${count} x ${unitPrice}`, price);
            if (discountPercent > 0) {
                pr.l('S', `   ${I18n.t('general.discount')} (${discountPercent}%)`, discountAmount);
                pr.l('S', `   ${I18n.t('general.with_discount')}`, toPay);
            }
        });
        const taxes = this.taxes.filter((taxItem) => !!taxItem.id || (!taxItem.id && taxItem.label.includes('VAT_')));
        if (taxes.length) {
            pr.l('D', '', { delimiter: '-' });
            // Taxes and Total Tax
            taxes.filter((taxItem) => !taxItem.id).forEach((taxItem) => {
                pr.l('S', `${I18n.t('order.exempt_from_tax')} ${taxItem.label.replace('VAT_', '')}:`, Money.parse(taxItem.price_before_tax).toFormat(priceFormat));
            });
            taxes.filter((taxItem) => !!taxItem.id).forEach((taxItem) => {
                pr.l('S', `${taxItem.label} ${I18n.t('general.price_pre_tax').toLowerCase()}:`, Money.parse(taxItem.price_before_tax).toFormat(priceFormat));
                pr.l('S', `${taxItem.label} (${parseFloat(taxItem.rate)}%):`, Money.parse(taxItem.value).toFormat(priceFormat));
            });
        }
        pr.l('D', '', { delimiter: '-' });
        const paymentTypes = this.getPayments();
        if (this.getExemptFromTax().toUnit() > 0) {
            pr.l('S', `${I18n.t('order.exempt_from_tax')}:`, this.getExemptFromTax().toFormat(priceFormat));
        }
        pr.l('S', `${I18n.t('general.price_pre_tax_total')}:`, this.getPriceBeforeTax().toFormat(priceFormat));
        pr.l('S', `${I18n.t('general.total_tax')}:`, this.getTax().toFormat(priceFormat));
        pr.l('D', '', { delimiter: '-' });
        // Price and Discount
        pr.l('S', `${I18n.t('general.total')}:`, this.getPrice().toFormat(priceFormat));
        pr.l('S', `${I18n.t('general.discount')}:`, this.getDiscountAmount().toFormat(priceFormat));
        pr.l('D', '', { delimiter: '-' });
        pr.l('S', `${I18n.t('general.to_pay').toUpperCase()}:`, this.getToPay().toFormat(priceFormat), { scale: '2,2' });
        paymentTypes.forEach(type => {
            pr.l('S', `${type.display}:`, Money.parse(type.amount).toFormat(priceFormat));
        });
        let note = this.note || '';
        const vatNote = vatExemptNote(resolvedVenue, '* ' + I18n.t('order.exempt_from_tax_comment'));
        if (vatNote) {
            note = vatNote + '\n\n' + note;
        }
        if (note) {
            pr.l('D', '', { delimiter: '-' });
            note.split('\n').forEach(line => pr.l('L', line));
        }
        pr.l('D', '', { delimiter: '-' });
        if (this.getPaidInvoice().toUnit() > 0) {
            pr.l('L', I18n.t('general.buyer'), { align: 'C' });
            pr.l();
            pr.l();
            pr.l('D', '', { delimiter: '-' });
        }
        if (receiptNote) {
            pr.l('D', '', { delimiter: '-' });
            receiptNote.split('\n').forEach(line => pr.l('L', line));
            pr.l('D', '', { delimiter: '-' });
        }
        const QRCode = await this.generateQRCodeString(venue);
        if (QRCode) {
            pr.l('QR', QRCode, { size: '40mm' });
        }
        const domain = I18n.c('general.domain');
        if (domain) {
            pr.l('L', `www.${domain}`, { align: 'C' });
        }
        return pr;
    }
    /**
     * Break down orders into several categories
     */
    static generateStats(orders) {
        const priceFormat = '0,0.00';
        if (I18n.c('order.unpaidIssuesReceipt') !== 'false') {
            orders = orders.filter(order => !(order.isUnpaid() && order.canceled()));
        }
        orders = orders.filter(order => !order.isTraining());
        orders = orders.filter(order => !order.isExternal());
        // Open orders only: an externally settled round (room charge) is not outstanding.
        const unpaidOrders = orders.filter(order => order.unpaidNoSummary() && !order.isSettledExternally());
        const paidOrders = orders.filter(order => order.isMoneyExchanged());
        const paidOrdersIncludingClosedAdvance = orders.filter(order => order.isPaid() || order.isSummary() || order.isAdvance());
        // cash calculation
        const cash = {
            sold: paidOrders.filter(order => !order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(priceFormat),
            refunded: paidOrders.filter(order => order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(priceFormat),
        };
        // payment calculation
        const payments = {
            cash: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(priceFormat),
            credit_card: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0)).toFormat(priceFormat),
            invoice: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidInvoice()), Money.parse(0)).toFormat(priceFormat),
            credit_card_business: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCreditCardBusiness()), Money.parse(0)).toFormat(priceFormat),
            check: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCheck()), Money.parse(0)).toFormat(priceFormat),
            voucher: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidVoucher()), Money.parse(0)).toFormat(priceFormat),
            mobile_money: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidMobileMoney()), Money.parse(0)).toFormat(priceFormat),
            other_noncash: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidOtherNoncash()), Money.parse(0)).toFormat(priceFormat),
            advance: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidAdvance()), Money.parse(0)).toFormat(priceFormat),
        };
        const total = {
            sold: paidOrdersIncludingClosedAdvance.filter(order => !order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(priceFormat),
            refunded: paidOrdersIncludingClosedAdvance.filter(order => order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(priceFormat),
            unpaid: unpaidOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(priceFormat),
        };
        return { cash, payments, total };
    }
    isImportedFromOldFiscalSystem() {
        return false;
    }
    shouldHaveNumber() {
        // External documents carry no receipt number anywhere — the server
        // nulls it on create, so numbering one locally (and advancing the
        // counter) would diverge from the stored record.
        if (this.isExternal())
            return false;
        return this.status != OrderStatusType.proforma && this.status != OrderStatusType.unpaid;
    }
    getExemptFromTaxReasons(item) {
        return OrderItem.exemptFromTaxReasons();
    }
    /**
     * Get last update of orders from server
     */
    static getLastUpdatedAt() {
        return Order.all().length
            ? moment(Math.max(...Order.all().filter(o => o.updated_at).map(o => moment(o.updated_at).valueOf())))
            : null;
    }
    static onTables() {
        return Order.all().filter(o => {
            return !!o.table_id
                && o.unpaidNoSummary()
                && !o.corrected()
                && !o.correctsOthers()
                && !o.canceled()
                && !o.isSettledExternally();
        });
    }
}
__decorate([
    LocaleOverride
], Order.prototype, "getRegisterRequest", null);
__decorate([
    LocaleOverride
], Order.prototype, "shouldRegister", null);
__decorate([
    LocaleOverride
], Order.prototype, "register", null);
__decorate([
    LocaleOverride
], Order.prototype, "shouldPrintReceipt", null);
__decorate([
    LocaleOverride
], Order.prototype, "getPaymentQRCode", null);
__decorate([
    LocaleOverride
], Order.prototype, "rendersManualInvoice", null);
__decorate([
    LocaleOverride
], Order.prototype, "getTaxes", null);
__decorate([
    LocaleOverride
], Order.prototype, "generateQRCodeString", null);
__decorate([
    LocaleOverride
], Order.prototype, "getTransactionDate", null);
__decorate([
    LocaleOverride
], Order.prototype, "getPriceBeforeTax", null);
__decorate([
    LocaleOverride
], Order.prototype, "getTax", null);
__decorate([
    LocaleOverride
], Order.prototype, "getPaid", null);
__decorate([
    LocaleOverride
], Order.prototype, "getIic", null);
__decorate([
    LocaleOverride
], Order.prototype, "getUnpaidOrderPrintable", null);
__decorate([
    LocaleOverride
], Order.prototype, "getExternallySettledSlipPrintable", null);
__decorate([
    LocaleOverride
], Order.prototype, "appendExternallySettledBlock", null);
__decorate([
    LocaleOverride
], Order.prototype, "getOrderPrintable", null);
__decorate([
    LocaleOverride
], Order.prototype, "isImportedFromOldFiscalSystem", null);
__decorate([
    LocaleOverride
], Order.prototype, "shouldHaveNumber", null);
__decorate([
    LocaleOverride
], Order.prototype, "getExemptFromTaxReasons", null);
__decorate([
    LocaleOverride
], Order, "fiscalStatuses", null);
export default Order;
modelRegistry.register('Order', Order);
//# sourceMappingURL=Order.js.map